import React, { useState, useEffect, useMemo } from 'react';
import AddEditPortfolioRealtimeModal from '../components/AddEditPortfolioRealtimeModal';
import GuruModal from '../components/GuruModal';
import LayoutPopup from '../components/LayoutPopup';
import { previewGuruExport, getGuruExportHistory, addGuruExportMark, createGuruExportHistory, listAdminPortfolios, getWeeklyRealtime, createGuru, deleteRealtimePortfolio, getGuru, deleteGuru, createPortfolioForGuru, deletePortfolioForGuru, createRealtimePortfolioAS, createRealtimePortfolioAP, createRealtimePortfolioADDUM, getRealtimePortfolio, sendTelegramBroadcastRealtimePortfolio, previewAdminPortfolioJSON, commitAdminPortfolioImportJSON, publishRealtimeV2Notification } from '../composables/fetchAPI'; // Import ฟังก์ชัน createGuru
import toast from 'react-hot-toast';
import { json } from 'stream/consumers';
import DonutChart from '../components/DonutChart';
import { Bell } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
type Portfolio = {
  id: string;
  name: string;
  description?: string;
};

type Guru = {
  id: string;
  name: string;
  portfolios: Portfolio[]; // เพิ่ม property portfolios
};

type Batch = {
  qty: number;
  price: number;
  createdAt: string;
  updatedAt: string;
};

type BuySell = {
  ticker: string;
  action: string;
  reason: string;
  narrative: string;
  proportion?: number;
  createdAt: string;
  updatedAt: string;
  batches: Batch[]; // เพิ่ม batches ในโครงสร้าง
};

type RealtimePortfolio = {
  guruId: string;
  portfolioName: string;
  guruType: string;
  createdAt: string;
  lastUpdated: string;
  telegramAnnounceAt: string | null;
  // ยังไม่แน่ใจว่า backend list endpoint ส่ง field พวกนี้มาด้วยหรือยัง (ดู notification-platform.md 11 ·
  // TIMESTAMP SEMANTICS) เผื่อไว้ optional — ถ้าไม่มี ปุ่มแจ้งเตือนใหม่จะถือว่ายังไม่เคยส่งช่องทางนั้น
  inAppAnnounceAt?: string | null;
  webPushAnnounceAt?: string | null;
  updatedAt: string;
  realtimeId: string;
  buys: BuySell[]; // ใช้โครงสร้าง BuySell
  sells: BuySell[]; // ใช้โครงสร้าง BuySell
  portfolioId: string; // ✅ เพิ่มบรรทัดนี้
  dateKey: string;     // ✅ เพิ่มบรรทัดนี้
};

// ---------- ปุ่มส่งแจ้งเตือน Realtime V2 (Notification Platform ใหม่) ----------
// อ้างอิง WeThaiInvest_Notification_Category_Type_Guide 06 · REALTIME V2 (PORTFOLIO / REALTIME_PUBLISHED)
// รูปแบบเดียวกับปุ่ม "ส่งแจ้งเตือน" ใน ArticlePage.tsx: เลือกช่องทางเป็น checkbox, ช่องทางที่เคยส่งแล้ว
// (มี xxxAnnounceAt) จะติ๊กไม่ได้ และถ้าส่งครบทั้ง 3 ช่องทางแล้วปุ่มจะหายไปเลย
// ต่างจาก Article ตรงที่ default ติ๊กมาให้แค่ TELEGRAM เพราะนี่คือ use case หลักของฟีเจอร์นี้
// (IN_APP/WEB_PUSH เป็นของใหม่ที่เพิ่งต่อเข้า core platform เลยไม่ได้ติ๊กมาให้อัตโนมัติ)
type RealtimeNotifyChannel = 'IN_APP' | 'WEB_PUSH' | 'TELEGRAM';
const REALTIME_NOTIFY_CHANNEL_OPTIONS: RealtimeNotifyChannel[] = ['IN_APP', 'WEB_PUSH', 'TELEGRAM'];

const RealtimeNotifyButton: React.FC<{
  realtimeId: string;
  portfolioName: string;
  inAppAnnounceAt?: string | null;
  webPushAnnounceAt?: string | null;
  telegramAnnounceAt?: string | null;
  onSent?: (channels: RealtimeNotifyChannel[]) => void;
}> = ({ realtimeId, portfolioName, inAppAnnounceAt, webPushAnnounceAt, telegramAnnounceAt, onSent }) => {
  const [sentChannels, setSentChannels] = useState<Record<RealtimeNotifyChannel, boolean>>({
    IN_APP: !!inAppAnnounceAt,
    WEB_PUSH: !!webPushAnnounceAt,
    TELEGRAM: !!telegramAnnounceAt,
  });
  const allChannelsSent = REALTIME_NOTIFY_CHANNEL_OPTIONS.every((c) => sentChannels[c]);

  const [notifyOpen, setNotifyOpen] = useState(false);
  // default ติ๊กเฉพาะ TELEGRAM (ถ้ายังไม่เคยส่ง) — ที่เหลือให้ admin เลือกเพิ่มเอง
  const [notifyChannels, setNotifyChannels] = useState<Record<RealtimeNotifyChannel, boolean>>({
    IN_APP: false,
    WEB_PUSH: false,
    TELEGRAM: !telegramAnnounceAt,
  });
  const [sendingNotify, setSendingNotify] = useState(false);

  if (allChannelsSent) return null;

  const handleSend = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const channels = REALTIME_NOTIFY_CHANNEL_OPTIONS.filter((c) => notifyChannels[c] && !sentChannels[c]);
    if (channels.length === 0) {
      toast.error('เลือกอย่างน้อย 1 ช่องทาง');
      return;
    }
    setSendingNotify(true);
    try {
      const res = await publishRealtimeV2Notification(realtimeId, channels);
      toast.success(`ส่งแจ้งเตือน "${portfolioName}" แล้ว (${res.status ?? 'QUEUED'})`);
      setSentChannels((prev) => {
        const next = { ...prev };
        channels.forEach((c) => { next[c] = true; });
        return next;
      });
      setNotifyChannels((prev) => {
        const next = { ...prev };
        channels.forEach((c) => { next[c] = false; });
        return next;
      });
      onSent?.(channels);
      setNotifyOpen(false);
    } catch (err: any) {
      toast.error(err.message || 'ส่งแจ้งเตือนไม่สำเร็จ');
    } finally {
      setSendingNotify(false);
    }
  };

  return (
    <div className="relative inline-block" onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        className="mr-4 bg-blue-500 text-white px-3 py-1 rounded font-bold hover:bg-blue-400 flex items-center gap-1"
        onClick={(e) => { e.preventDefault(); setNotifyOpen((v) => !v); }}
        title="ส่งแจ้งเตือน Realtime นี้"
      >
        <Bell size={14} /> ส่งแจ้งเตือน
      </button>

      {notifyOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={(e) => { e.preventDefault(); e.stopPropagation(); setNotifyOpen(false); }} />
          <div
            className="absolute right-0 top-full mt-2 w-56 rounded-xl shadow-2xl p-3 text-sm z-50"
            style={{ backgroundColor: 'var(--component-bg-color, #18181b)', color: 'var(--text-color, #fff)', border: '1px solid var(--bg-item, #333)' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="font-bold mb-2 text-xs opacity-80">ส่งแจ้งเตือนพอร์ตนี้</div>
            <div className="flex flex-col gap-1.5 mb-3">
              {REALTIME_NOTIFY_CHANNEL_OPTIONS.map((ch) => {
                const alreadySent = sentChannels[ch];
                return (
                  <label
                    key={ch}
                    className="flex items-center gap-2 text-xs cursor-pointer"
                    style={alreadySent ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
                  >
                    <input
                      type="checkbox"
                      disabled={alreadySent}
                      checked={notifyChannels[ch]}
                      onChange={(e) => setNotifyChannels((prev) => ({ ...prev, [ch]: e.target.checked }))}
                    />
                    {ch}
                    {alreadySent && <span className="opacity-70">(ส่งแล้ว)</span>}
                  </label>
                );
              })}
            </div>
            {notifyChannels.TELEGRAM && !sentChannels.TELEGRAM && (
              <p className="text-[10px] opacity-60 mb-2">
                TELEGRAM ยังส่งผ่าน legacy broadcast เดิม (template AS/AD/AP) ไม่ใช่ generic renderer ของ platform ใหม่
              </p>
            )}
            <div className="flex gap-2">
              <button
                type="button"
                disabled={sendingNotify}
                onClick={handleSend}
                className="flex-1 py-1.5 rounded-lg text-xs font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
                style={{ backgroundColor: 'var(--button-create-bg, #22c55e)', color: 'var(--button-text-color, #000)' }}
              >
                {sendingNotify ? 'กำลังส่ง...' : 'ส่งแจ้งเตือน'}
              </button>
              <button
                type="button"
                disabled={sendingNotify}
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); setNotifyOpen(false); }}
                className="px-3 py-1.5 rounded-lg text-xs font-semibold disabled:opacity-40"
                style={{ backgroundColor: 'var(--bg-item, #333)' }}
              >
                ยกเลิก
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const RealtimePageV2: React.FC<{ userInfo?: any }> = ({ userInfo }) => {
  // รองรับ deep link จากแจ้งเตือน Realtime V2 เช่น /realtime?realtimeId=...&dateKey=YYYYMMDD
  // (ดู WeThaiInvest_Notification_Category_Type_Guide 06 · REALTIME V2) — ต้องโหลดข้อมูล "ของวันนั้น"
  // ไม่ใช่ default ไปที่ข้อมูลล่าสุดเหมือนก่อนหน้านี้ (ตัว dateKey นี้ไม่เปลี่ยนหลัง mount แล้ว ไม่ต้อง sync ซ้ำ)
  const [searchParams] = useSearchParams();
  const urlDateKey = searchParams.get('dateKey');
  const urlRealtimeId = searchParams.get('realtimeId');
  const [showLayoutPopup, setShowLayoutPopup] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showGuruModal, setShowGuruModal] = useState(false);
  const [gurus, setGurus] = useState<Guru[]>([]);
  const [realtimePortfolios, setRealtimePortfolios] = useState<RealtimePortfolio[]>([]);
  const [selectedGuruId, setSelectedGuruId] = useState<string | null>("2d51d1c5-e764-4503-9293-3a98c5d39123");
  const [editPortfolio, setEditPortfolio] = useState<RealtimePortfolio | null>(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const hoverTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const [hoverData, setHoverData] = useState<any | null>(null);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [isAdDumNoteExpanded, setIsAdDumNoteExpanded] = useState(false);
  const [weeklyDates, setWeeklyDates] = useState<{ timeCode: string; message: string }[]>([]);
  const [selectedDateKey, setSelectedDateKey] = useState<string>("");
  // ✅ 1. เพิ่ม State สำหรับ Sync
  const [showSyncModal, setShowSyncModal] = useState(false);
  const [syncTargetPortfolio, setSyncTargetPortfolio] = useState<RealtimePortfolio | null>(null);
  const [syncedPortfolioKeys, setSyncedPortfolioKeys] = useState<string[]>([]);
  // ✅ เพิ่ม State สำหรับ Modal ดูประวัติการ Sync (Mark History)
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [exportHistoryList, setExportHistoryList] = useState<any[]>([]);
  const [previewData, setPreviewData] = useState<any>(null); // ✅ เก็บผลลัพธ์จาก API Preview
  // ✅ State สำหรับแสดงรายละเอียดหุ้น (ซื้อ-ขาย-จำนวน) ในการ์ดประวัติ Sync (โหลดอัตโนมัติ ไม่ต้องกด)
  const [historyDetailsMap, setHistoryDetailsMap] = useState<Record<number, any[]>>({});
  const [loadingHistorySet, setLoadingHistorySet] = useState<Set<number>>(new Set());
  const [adminShayPortfolios, setAdminShayPortfolios] = useState<any[]>([]);
  const [selectedAdminShayPortfolioId, setSelectedAdminShayPortfolioId] = useState<string>("");
  // 1. เพิ่ม State นี้ไว้ที่ด้านบนของ Component RealtimePageV2
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
  // ✅ State ฟอร์มสำหรับ Bulk Sync
  const [syncForm, setSyncForm] = useState({
    guruId: '5f8adbab-f7ed-4564-9c18-43e81f74c244', // ค่า Default (แอดอั้ม หรือจารย์ Shay ตามสะดวก)
    sourcePortfolioId: '',
    fromDate: new Date().toISOString().split('T')[0], // ตั้งต้นเป็นวันนี้ YYYY-MM-DD
    toDate: new Date().toISOString().split('T')[0],
  });

  // พอเปิดหน้า Modal History ปุ๊บ ให้ดึงประวัติทันที
  useEffect(() => {
    if (showSyncModal && userInfo?.isAdmin) {
      const fetchHistory = async () => {
        try {
          const res = await getGuruExportHistory();
          setExportHistoryList(res.items || res.data?.items || []);
        } catch (err) { }
      };
      fetchHistory();
    }
  }, [showSyncModal, userInfo]);

  const handleMouseEnter = (item: any, e: React.MouseEvent) => {
    if (window.innerWidth < 1024) return;
    const { clientX, clientY } = e;

    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);

    hoverTimeoutRef.current = setTimeout(() => {
      setHoverData(item);
      setMousePos({ x: clientX, y: clientY });
    }, 300);
  };

  useEffect(() => {
    if (userInfo?.isAdmin) {
      const fetchExportHistory = async () => {
        try {
          const res = await getGuruExportHistory();
          const historyItems = res.items || res.data?.items || [];

          // กรองหา item ที่มี marks ประเภท UPLOAD_COMPLETED
          const completedKeys: string[] = [];
          historyItems.forEach((item: any) => {
            const hasCompleted = item.marks?.some((m: any) => m.type === 'UPLOAD_COMPLETED');
            if (hasCompleted) {
              // เก็บเอกลักษณ์ เช่น จับคู่จาก portfolioId + fromDate
              completedKeys.push(`${item.portfolioId}_${item.fromDate}`);
            }
          });

          setSyncedPortfolioKeys(completedKeys);
        } catch (error) {
          console.error('Error fetching export history:', error);
        }
      };
      fetchExportHistory();
    }
  }, [userInfo, realtimePortfolios]);

  useEffect(() => {
    if (userInfo?.isAdmin) {
      const fetchAdminPorts = async () => {
        try {
          const res = await listAdminPortfolios();

          // ✅ 1. เข้าถึงข้อมูลให้ถูก Path (เพิ่ม .data เข้าไป)
          const allPorts = res.data?.portfolios || res.portfolios || [];

          // ✅ 2. คอมเมนต์หรือลบเงื่อนไข Filter ชั่วคราว เพื่อให้พอร์ต "Admin Portfolio API Test" แสดงขึ้นมา
          // const shayPorts = allPorts.filter((p: any) => p.name.toLowerCase().includes('shay'));
          const targetPorts = allPorts;

          setAdminShayPortfolios(targetPorts);

          // ถ้ามีพอร์ต ให้ตั้งเป็นค่า Default ไว้ตัวแรก (เฉพาะตอนที่ยังไม่มีใครเลือกไว้ก่อน
          // กัน Race Condition: ถ้า Fetch นี้ช้า แล้วผู้ใช้เลือกพอร์ตปลายทางไปแล้วก่อนที่ค่านี้จะโหลดเสร็จ
          // ต้องไม่ไปทับค่าที่ผู้ใช้เลือกไว้)
          if (targetPorts.length > 0) {
            setSelectedAdminShayPortfolioId(prev => prev || targetPorts[0].adminPortfolioId);
          }
        } catch (error) {
          console.error('Error fetching admin portfolios:', error);
        }
      };
      fetchAdminPorts();
    }
  }, [userInfo]);
  // ⚠️ เปลี่ยนเป็น ID พอร์ตแอดมินจารย์เชย์จริงๆ ของคุณ
  const targetAdminPortfolioId = "ADMPORT-XXXXXXXXX";
  const handleOpenHistoryModal = async () => {
    try {
      toast.loading('กำลังโหลดประวัติการ Sync...', { id: 'history-toast' });
      const res = await getGuruExportHistory();
      const items = res.items || res.data?.items || [];

      setExportHistoryList(items);
      setShowHistoryModal(true);
      toast.success('โหลดประวัติสำเร็จ', { id: 'history-toast' });
    } catch (error) {
      console.error('Error fetching history list:', error);
      toast.error('ไม่สามารถโหลดประวัติได้', { id: 'history-toast' });
    }
  };

  // ✅ ดึงรายละเอียดหุ้นที่ซื้อ-ขายของการ์ดประวัติ Sync แต่ละรายการ (ดึงจาก Preview API ตามช่วงวันที่ของรายการนั้นๆ)
  const fetchHistoryDetail = async (item: any, idx: number) => {
    try {
      setLoadingHistorySet(prev => new Set(prev).add(idx));
      const res = await previewGuruExport({
        guruId: item.guruId,
        portfolioId: item.portfolioId,
        fromDate: item.fromDate,
        toDate: item.toDate,
      });
      const rows = res.data?.previewRows || res.previewRows || [];
      setHistoryDetailsMap(prev => ({ ...prev, [idx]: rows }));
    } catch (error) {
      console.error('Error fetching history detail rows:', error);
      setHistoryDetailsMap(prev => ({ ...prev, [idx]: [] }));
    } finally {
      setLoadingHistorySet(prev => {
        const next = new Set(prev);
        next.delete(idx);
        return next;
      });
    }
  };

  // ✅ โหลดรายละเอียดหุ้นให้ทุกการ์ดอัตโนมัติทันทีที่ประวัติ Sync ถูกดึงมา (ไม่ต้องกดดูเพิ่ม)
  useEffect(() => {
    exportHistoryList.forEach((item: any, idx: number) => {
      if (!historyDetailsMap[idx] && item.guruId && item.portfolioId) {
        fetchHistoryDetail(item, idx);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exportHistoryList]);
  // --- ฟังก์ชันที่ 1: ดึงข้อมูลมายิง Preview ---
  const handlePreviewSync = async () => {
    if (!selectedAdminShayPortfolioId) {
      toast.error('กรุณาเลือกพอร์ตแอดมินปลายทาง', { id: 'sync-toast' });
      return;
    }
    if (!syncForm.sourcePortfolioId) {
      toast.error('กรุณาเลือกพอร์ตต้นทาง', { id: 'sync-toast' });
      return;
    }
    if (syncForm.fromDate > syncForm.toDate) {
      toast.error('วันที่เริ่มต้นต้องไม่มากกว่าวันที่สิ้นสุด', { id: 'sync-toast' });
      return;
    }

    try {
      toast.loading('กำลังดึงข้อมูลและตรวจสอบ Preview...', { id: 'sync-toast' });

      // 1. ดึงข้อมูล Preview จาก Guru Export API (แปลงวันที่เป็น YYYYMMDD)
      const exportRes = await previewGuruExport({
        guruId: syncForm.guruId,
        portfolioId: syncForm.sourcePortfolioId,
        fromDate: syncForm.fromDate.replace(/-/g, ''),
        toDate: syncForm.toDate.replace(/-/g, '')
      });

      const previewRows = exportRes.data?.previewRows || exportRes.previewRows || [];
      if (previewRows.length === 0) {
        toast.error('ไม่พบรายการซื้อขายในช่วงเวลาที่เลือก', { id: 'sync-toast' });
        return;
      }

      // 2. แปลงข้อมูลที่ได้ให้ตรงสเปกของ Admin Portfolio JSON Import
      const items = previewRows.map((r: any) => ({
        ticker: r.ticker.toUpperCase(),
        action: r.action,
        qty: r.qty,
        price: r.price,
        transactionAt: new Date().toISOString(), // ถ้า API ไม่คืนเวลามา ให้ใช้เวลาปัจจุบันเป็น Fallback
        remark: r.remark || "Bulk Sync from Realtime"
      }));

      // 3. ยิงเช็ค Preview ไปที่พอร์ตแอดมินปลายทาง
      const previewPayload = {
        mode: "APPEND_TRANSACTIONS",
        cashDeposit: 0,
        cashWithdraw: 0,
        asofDate: new Date().toISOString(),
        timezone: "Asia/Bangkok",
        idempotencyKey: `bulk-sync-${Date.now()}`,
        items: items
      };

      const adminPreviewRes = await previewAdminPortfolioJSON(selectedAdminShayPortfolioId, previewPayload);
      setPreviewData(adminPreviewRes);
      toast.success('ตรวจสอบ Preview สำเร็จ', { id: 'sync-toast' });

    } catch (error: any) {
      console.error('Preview Error:', error);
      const errPayload = error?.error?.details || error?.data || error;
      setPreviewData(errPayload);
      toast.error('พบข้อผิดพลาดในการตรวจสอบ Preview', { id: 'sync-toast' });
    }
  };

  // --- ฟังก์ชันที่ 2: ยืนยันการบันทึก (Commit) (Bulk) ---
  const handleCommitSync = async () => {
    const token = previewData?.data?.previewToken || previewData?.previewToken;
    if (!token || !selectedAdminShayPortfolioId) return;

    try {
      toast.loading('กำลัง Commit ข้อมูลลงระบบ...', { id: 'sync-toast' });

      // 1. Commit ข้อมูลเข้าพอร์ตแอดมิน
      const commitRes = await commitAdminPortfolioImportJSON(selectedAdminShayPortfolioId, {
        previewToken: token
      });

      // 2. สร้างประวัติ (Export History) 
      const exportRes = await createGuruExportHistory({
        guruId: syncForm.guruId,
        portfolioId: syncForm.sourcePortfolioId,
        fromDate: syncForm.fromDate.replace(/-/g, ''),
        toDate: syncForm.toDate.replace(/-/g, ''),
        remark: "Bulk Auto Sync via UI"
      });

      // 3. แปะป้าย Mark
      if (exportRes?.data?.export?.exportId || exportRes?.export?.exportId) {
        const exportId = exportRes?.data?.export?.exportId || exportRes?.export?.exportId;
        await addGuruExportMark(exportId, {
          type: "UPLOAD_COMPLETED",
          remark: "นำเข้าพอร์ตแอดมินแบบกลุ่มหลายวัน",
          metadata: {
            targetAdminPortfolioId: selectedAdminShayPortfolioId,
            importJobId: commitRes.importJobId || commitRes.data?.importJobId
          }
        });
      }

      toast.success('🚀 Sync ข้อมูลเรียบร้อยแล้ว!', { id: 'sync-toast' });

      // รีเฟรชประวัติ
      const histRes = await getGuruExportHistory();
      setExportHistoryList(histRes.items || histRes.data?.items || []);
      // ==========================================
      // 🌟 สั่งปิด Modal และเคลียร์ค่าทั้งหมดตรงนี้เลย
      // ==========================================
      setPreviewData(null);
      setShowSyncModal(false); // ปิด Popup ทันที

    } catch (error: any) {
      console.error('Commit Error:', error);
      const errMsg = error?.errors?.[0]?.message || error?.message || 'เกิดข้อผิดพลาดในการ Commit';
      toast.error(`Commit ไม่สำเร็จ: ${errMsg}`, { id: 'sync-toast' });
    }
  };

  const handleItemClick = (item: any, e: React.MouseEvent | React.PointerEvent) => {
    if (window.innerWidth < 1024) {
      // หยุดการกระจาย Event เพื่อไม่ให้ไปโดน Mouse Event อื่นๆ
      e.preventDefault();

      const { clientX, clientY } = e;

      // เช็คว่าเป็นตัวเดิมหรือไม่ (ใช้ ticker + action + batch index ถ้ามี)
      const isSameItem = hoverData &&
        hoverData.ticker === item.ticker &&
        hoverData.action === item.action;

      if (isSameItem) {
        setHoverData(null); // ถ้ากดซ้ำตัวเดิมให้ปิด
      } else {
        if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
        setHoverData(item); // เปิดตัวใหม่
        setMousePos({ x: clientX, y: clientY });
      }
    }
  };


  const batchColors = [
    '#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899', '#06b6d4',
    '#f87171', '#fb923c', '#a3e635', '#2dd4bf', '#818cf8', '#c084fc',
    '#fb7185', '#94a3b8', '#fbbf24', '#34d399', '#60a5fa', '#a78bfa',
    '#f472b6', '#4ade80'
  ];
  // ฟังก์ชันสำหรับสุ่มสีจาก String (Timestamp)
  // ฟังก์ชันสำหรับดึงสีตามลำดับการแก้ไข
  const getSequenceColor = (currentTime: string | undefined, portfolio: any) => {
    if (!currentTime) return '#374151';

    const allBuys = portfolio.buys || [];
    const allSells = portfolio.sells || [];

    // แก้ไข: ให้รวบรวมเวลาทั้งจาก batches (ถ้ามี) และจาก updatedAt ของตัวหุ้นเอง (สำหรับแอดอั้ม)
    const allTimestamps = Array.from(new Set([
      // ดึงจาก batches
      ...allBuys.flatMap((h: any) => h.batches?.map((b: any) => b.createdAt?.substring(0, 16))),
      ...allSells.flatMap((h: any) => h.batches?.map((b: any) => b.createdAt?.substring(0, 16))),
      // ดึงจาก updatedAt ของตัวหุ้น (กรณีไม่มี batches เช่น แอดอั้ม)
      ...allBuys.map((h: any) => h.updatedAt?.substring(0, 16)),
      ...allSells.map((h: any) => h.updatedAt?.substring(0, 16))
    ]))
      .filter(Boolean)
      .sort();

    const currentKey = currentTime.substring(0, 16);
    const sequenceIndex = allTimestamps.indexOf(currentKey);

    if (sequenceIndex === -1) return '#374151';

    return batchColors[sequenceIndex % batchColors.length];
  };
  const handleMouseLeave = () => {
    if (window.innerWidth < 1024) return;

    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);

    // ทริคเพิ่มเติม: แนะนำให้ปรับเวลาตรงนี้เป็นสัก 300ms หรือ 400ms 
    // เพื่อให้ User มีเวลาขยับเมาส์ข้ามช่องว่างมาที่ Popup ได้ทันครับ
    hoverTimeoutRef.current = setTimeout(() => {
      setHoverData(null);
    }, 300);
  };
  const handlePopupMouseEnter = () => {
    // พอมันเป็น useRef มันจะเห็น ID ล่าสุดและเบรกการปิดได้ทันทีครับ
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
  };

  // const fetchRealtimePortfolio = async () => {
  //   try {
  //     const data = await getRealtimePortfolio();
  //     setRealtimePortfolios(data);
  //     console.log('Realtime Portfolio Data:', data);
  //   } catch (error) {
  //     console.error('Error fetching realtime portfolio:', error);
  //   }
  // };
  // useEffect(() => {
  //   fetchRealtimePortfolio();
  // }, []); // เพิ่ม dependency array เป็น [] เพื่อป้องกันการเรียกซ้ำ

  useEffect(() => {
    const fetchGurus = async () => {
      try {
        const response = await getGuru();
        setGurus(response);
      } catch (error) {
        console.error('Error fetching gurus:', error);
        toast.error('ไม่สามารถดึงข้อมูลกูรูได้');
      }
    };
    fetchGurus();
  }, []);

  const fetchRealtimePortfolioData = async (dateKey?: string) => {
    try {
      const data = await getRealtimePortfolio(dateKey); // ใช้ฟังก์ชันที่แก้ไปก่อนหน้านี้
      setRealtimePortfolios(data);
    } catch (error) {
      console.error('Error fetching realtime portfolio:', error);
    }
  };

  useEffect(() => {
    const loadInitialData = async () => {
      try {
        const dates = await getWeeklyRealtime(); // ดึงลิสต์วันที่
        // endpoint นี้คืนแค่ ~7 วันล่าสุด ถ้า dateKey จาก URL เก่ากว่านั้นจะไม่มีใน list — แทรก option
        // ของวันนั้นเข้าไปเองกัน dropdown โชว์ค่าว่างทั้งที่ข้อมูลจริง ๆ โหลดถูกวันแล้ว
        const effectiveDates = urlDateKey && !dates?.some((d: any) => d.timeCode === urlDateKey)
          ? [{ timeCode: urlDateKey, message: `บันทึกวันที่ ${urlDateKey}` }, ...(dates || [])]
          : dates;
        setWeeklyDates(effectiveDates);

        if (effectiveDates && effectiveDates.length > 0) {
          // มาจากแจ้งเตือนที่ระบุ dateKey มาให้ → ต้องโหลดข้อมูลของวันนั้น ไม่ใช่ default ไปที่ล่าสุด
          setSelectedDateKey(urlDateKey || effectiveDates[0].timeCode);
          // ไม่ต้องเรียก fetchRealtimePortfolioData ตรงนี้ เพราะจะให้ useEffect ด้านล่างจัดการ
        }
      } catch (error) {
        console.error('Error loading weekly dates:', error);
      }
    };
    loadInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // เมื่อโหลดข้อมูลของวันที่ตรงกับ deep link มาแล้ว เลื่อนหน้าจอไปโฟกัสการ์ดพอร์ตที่ notification ระบุมา
  useEffect(() => {
    if (!urlRealtimeId) return;
    const el = document.getElementById(`realtime-card-${urlRealtimeId}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [realtimePortfolios, urlRealtimeId]);
  // useEffect สำหรับโหลดข้อมูลพอร์ตทุกครั้งที่เปลี่ยนวันที่
  useEffect(() => {
    if (selectedDateKey) {
      fetchRealtimePortfolioData(selectedDateKey);
    }
  }, [selectedDateKey]);

  // ส่วนการกรองและเรียงลำดับข้อมูล
  const displayPortfolios = useMemo(() => {
    // กรอง 24 ชม. เฉพาะตอนที่เป็น "ข้อมูลล่าสุด" หรือจะเอาออกถาวรเพื่อให้ดูย้อนหลังได้ทุกอัน
    return realtimePortfolios
      // .filter(p => (new Date().getTime() - new Date(p.lastUpdated).getTime()) <= 86400000) // <-- ลบหรือ Comment บรรทัดนี้ออก
      .sort((a, b) => new Date(b.lastUpdated).getTime() - new Date(a.lastUpdated).getTime());
  }, [realtimePortfolios]);
  const handleCreateRealtimePortfolio = async (
    guruId: string,
    portId: string,
    portfolioData: any
  ) => {
    try {
      let response;

      // ตรวจสอบเงื่อนไขของ Guru ID
      if (guruId === 'eba14023-edb7-4fed-aa59-0dd2ae07de46') {
        response = await createRealtimePortfolioAP(guruId, portId, portfolioData);
      } else if (guruId === '2d51d1c5-e764-4503-9293-3a98c5d39123') {
        response = await createRealtimePortfolioADDUM(guruId, portId, portfolioData);
      } else {
        // Default case: ใช้ createRealtimePortfolioADDUM
        response = await createRealtimePortfolioAS(guruId, portId, portfolioData.holdings);
      }

      // toast.success('สร้าง Realtime Portfolio สำเร็จ');
      // console.log('Realtime Portfolio created:', response);

      // โหลดข้อมูล Guru ใหม่
      const updatedGurus = await getGuru();
      setGurus(updatedGurus); // อัปเดตลิสต์ Guru
    } catch (error) {
      console.error('Error creating realtime portfolio:', error);
      toast.error('ไม่สามารถสร้าง Realtime Portfolio ได้');
    }

  };
  const handleDeleteRealtimePortfolio = async (realtimeId: string) => {
    try {
      await deleteRealtimePortfolio(realtimeId);
      toast.success('ลบพอร์ตเรียบร้อยแล้ว');
      // อัปเดตข้อมูลหลังจากลบสำเร็จ
      fetchRealtimePortfolioData(selectedDateKey);
    } catch (error) {
      console.error('Error deleting realtime portfolio:', error);
      toast.error('ไม่สามารถลบพอร์ตได้');
    }
  };
  const handleDeleteGuru = async (guruId: string) => {
    try {
      await deleteGuru(guruId);
      toast.success('ลบ Guru สำเร็จ');
      // โหลดข้อมูล Guru ใหม่
      const updatedGurus = await getGuru();
      setGurus(updatedGurus); // อัปเดตลิสต์ Guru
    } catch (error) {
      console.error('Error deleting guru:', error);
      toast.error('ไม่สามารถลบ Guru ได้');
    }
  };

  const handleDeletePortfolio = async (guruId: string, portfolioId: string) => {
    try {
      await deletePortfolioForGuru(guruId, portfolioId);
      // โหลดข้อมูล Guru ใหม่
      const updatedGurus = await getGuru();
      setGurus(updatedGurus); // อัปเดตลิสต์ Guru
    } catch (error) {
      console.error('Error deleting portfolio:', error);
      toast.error('ไม่สามารถลบพอร์ตได้');
    }
  };




  // เดิมเป็นปุ่มเดียวที่ยิงตรงไป legacy broadcast (/broadcast/realtime/:id/announce) เฉพาะ Telegram
  // ตอนนี้แทนที่ในหน้าจอด้วย <RealtimeNotifyButton> ที่ไปทาง /realtime-v2/:id/publish แทน (รองรับ
  // IN_APP/WEB_PUSH ผ่าน Notification Platform ใหม่ด้วย ส่วน TELEGRAM ฝั่ง backend ยังวิ่ง legacy
  // broadcast เดิมอยู่ดีตามที่คู่มือระบุ) เก็บฟังก์ชันนี้ไว้เผื่อ rollback ไม่ได้ลบ แต่ไม่มีปุ่มเรียกใช้แล้ว
  const handleSendToTelegram = async (realtimeId: string) => {
    const portfolio = realtimePortfolios.find((p) => p.realtimeId === realtimeId);
    const portfolioName = portfolio?.portfolioName || 'Unknown Portfolio';

    // แสดงข้อความยืนยันก่อนส่ง
    toast((t) => (
      <div>
        <p>คุณต้องการส่งพอร์ต <strong>{portfolioName}</strong> ไปยัง Telegram ใช่หรือไม่?</p>
        <div className="flex justify-end gap-2 mt-2">
          <button
            className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
            onClick={async () => {
              toast.dismiss(t.id); // ปิดข้อความยืนยัน
              try {
                // เรียกใช้ API เพื่อส่งข้อมูลไปยัง Telegram
                await sendTelegramBroadcastRealtimePortfolio(realtimeId);

                // แสดงข้อความสำเร็จ
                toast.success(`สำเร็จ! Portfolio "${portfolioName}" ถูกส่งไปยัง Telegram เรียบร้อยแล้ว!`);

                // อัปเดตสถานะ isAnnounced ใน state
                setRealtimePortfolios((prev) =>
                  prev.map((p) =>
                    p.realtimeId === realtimeId
                      ? { ...p, telegramAnnounceAt: new Date().toISOString() }
                      : p
                  )
                );
              } catch (error) {
                // แสดงข้อความเมื่อเกิดข้อผิดพลาด
                console.error('Error sending portfolio to Telegram:', error);
                toast.error('ไม่สามารถส่ง Portfolio ไปยัง Telegram ได้');
              }
            }}
          >
            ใช่
          </button>
          <button
            className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
            onClick={() => toast.dismiss(t.id)} // ปิดข้อความยืนยัน
          >
            ยกเลิก
          </button>
        </div>
      </div>
    ));
  };

  const handleCreatePortfolio = async (guruId: string, portfolioName: string) => {
    try {
      const response = await createPortfolioForGuru(guruId, { name: portfolioName });
      toast.success('เพิ่มพอร์ตสำเร็จ');
      // console.log('Portfolio created:', response);
      // โหลดข้อมูล Guru ใหม่
      const updatedGurus = await getGuru();
      setGurus(updatedGurus); // อัปเดตลิสต์ Guru
    } catch (error) {
      console.error('Error creating portfolio:', error);
      toast.error('ไม่สามารถเพิ่มพอร์ตได้');
    }
  };

  // ฟังก์ชันสำหรับสร้าง Guru
  const handleCreateGuru = async (guruData: { name: string; description?: string }) => {
    try {
      const response = await createGuru(guruData);
      toast.success('Guru created successfully:', response);
      // window.location.reload(); // รีโหลดหน้า
    } catch (error) {
      console.error('Error creating guru:', error);
    }
  };
  const handleGuruChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedGuruId(e.target.value);
  };

  const filteredPortfolios = realtimePortfolios.filter(
    (portfolio) => portfolio.guruId === selectedGuruId
  );

  const hasNewPortfolio = (guruId: string) => {
    return realtimePortfolios.some(
      (portfolio) =>
        portfolio.guruId === guruId &&
        new Date().getTime() - new Date(portfolio.lastUpdated).getTime() <= 24 * 60 * 60 * 1000
    );
  };

  return (
    <div
      className="p-2 sm:p-4 md:p-8 min-h-screen text-white"
      style={{ background: 'var(--bg-color)' }}
    >
      <h2 className="text-lg sm:text-xl font-bold mb-4" style={{ color: 'var(--cyan-text-color)' }}>
        มุมมองของผู้ใช้ (ก่อน 17:00 น.)
      </h2>

      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-2">
        {/* Container หลักที่ใช้จัด Layout 3 กล่อง */}
        {!userInfo?.isAdmin && (
          <div className="mb-1 w-full">
            {/* --- MOBILE VIEW: เลื่อนขวา 3 กล่อง + ดูเพิ่มเติมแยกกัน (แสดงเฉพาะจอเล็ก) --- */}
            <div className="lg:hidden flex overflow-x-auto gap-4 pb-2 snap-x no-scrollbar">

              {/* กล่องที่ 1: Note */}
              <div className="min-w-[85vw] snap-center">
                <div className="bg-white/5 border-l-4 border-yellow-500/50 p-4 rounded-r-lg h-full transition-all"
                  style={{ background: 'var(--component-bg-color)' }}>
                  <span className="text-yellow-500 font-bold block mb-1 text-sm">⚠️ Note:</span>
                  <p className={`text-[13px] leading-relaxed font-light ${expandedIndex !== 0 ? 'truncate' : 'whitespace-normal'}`}
                    style={{ color: 'var(--text-color)' }}>
                    "ข้อมูลและไอเดียการเทรดทั้งหมดนี้ เป็นเพียงกรณีศึกษาเพื่อนำไปต่อยอดเท่านั้น ไม่ใช่การชี้นำต้องลงทุน เพื่อนๆควรนำไปศึกษาทำการบ้านเพิ่มเติมเพื่อตัดสินใจด้วยตัวเอง ต้องไม่ลืมว่า <span className="text-yellow-500/80 font-medium">"ต้นทุนค่าเฉลี่ย, เงินสำรอง, การแบ่งไม้เข้าซื้อ, กลยุทธ์ รวมถึงการรับความเสี่ยง"</span> ของเราแต่ละคน <strong className="text-yellow-500/80 mx-1">**ไม่เท่ากัน**</strong> การลงทุนที่ดีควรยึดเป้าหมาย และหน้าตักพอร์ตของเราเป็นหลักเสมอครับ"
                  </p>
                  <button
                    onClick={() => setExpandedIndex(expandedIndex === 0 ? null : 0)}
                    className="text-blue-400 text-[12px] font-bold mt-2 hover:text-blue-300"
                  >
                    {expandedIndex === 0 ? "...ดูน้อยลง" : "...ดูเพิ่มเติม"}
                  </button>
                </div>
              </div>

              {/* กล่องที่ 2: Tips 1 */}
              <div className="min-w-[85vw] snap-center">
                <div className="bg-white/5 border-l-4 border-blue-500/50 p-4 rounded-r-lg h-full transition-all"
                  style={{ background: 'var(--component-bg-color)' }}>
                  <span className="text-blue-400 font-bold block mb-1 text-sm">🎨 Tips:</span>
                  <p className={`text-[13px] leading-relaxed font-light ${expandedIndex !== 1 ? 'truncate' : 'whitespace-normal'}`}
                    style={{ color: 'var(--text-color)' }}>
                    "สีของกรอบหุ้นจะ <strong className="text-blue-400/90 mx-1">เปลี่ยนไปตามรอบการอัปเดต</strong> โดยหุ้นที่มีสีของกรอบเหมือนกัน หมายถึงหุ้นกลุ่มที่ถูกปรับปรุงข้อมูลพร้อมกันในรอบนั้นๆ เพื่อให้เพื่อนๆ สังเกตการเปลี่ยนแปลงล่าสุดได้ง่ายขึ้นครับ"
                  </p>
                  <button
                    onClick={() => setExpandedIndex(expandedIndex === 1 ? null : 1)}
                    className="text-blue-400 text-[12px] font-bold mt-2 hover:text-blue-300"
                  >
                    {expandedIndex === 1 ? "...ดูน้อยลง" : "...ดูเพิ่มเติม"}
                  </button>
                </div>
              </div>

              {/* กล่องที่ 3: Tips 2 */}
              <div className="min-w-[85vw] snap-center">
                <div className="bg-white/5 border-l-4 border-green-500/50 p-4 rounded-r-lg h-full transition-all"
                  style={{ background: 'var(--component-bg-color)' }}>
                  <span className="text-green-500 font-bold block mb-1 text-sm">🔔 Telegram Alert:</span>
                  <p className={`text-[13px] leading-relaxed font-light ${expandedIndex !== 2 ? 'truncate' : 'whitespace-normal'}`}
                    style={{ color: 'var(--text-color)' }}>
                    "สำหรับเพื่อนๆ ที่อยากเกาะติดพอร์ตการลงทุนให้ทันท่วงที <strong className="text-green-500 font-medium">ผมเปิดระบบแจ้งเตือนผ่าน Telegram ให้ใช้งานฟรี</strong> เพื่อให้เพื่อนๆ ไม่พลาดทุกไม้ที่ Guru มีการเคลื่อนไหวแบบ <strong className="text-green-500 font-medium">Real-time</strong> ครับ" สามารถคลิกที่โปรไฟล์บนขวาเพื่อลงทะเบียน Telegram ได้เลย
                  </p>
                  <button
                    onClick={() => setExpandedIndex(expandedIndex === 2 ? null : 2)}
                    className="text-blue-400 text-[12px] font-bold mt-2 hover:text-blue-300"
                  >
                    {expandedIndex === 2 ? "...ดูน้อยลง" : "...ดูเพิ่มเติม"}
                  </button>
                </div>
              </div>
            </div>

            {/* --- DESKTOP VIEW: แสดง 3 กล่องปกติ (แสดงเฉพาะจอใหญ่ lg ขึ้นไป) --- */}
            <div className="hidden lg:flex flex-row gap-4 items-stretch">
              {/* ฝั่งซ้าย: Note */}
              <div className="flex-1">
                <div className="bg-white/5 border-l-4 border-yellow-500/50 p-4 rounded-r-lg h-full"
                  style={{ background: 'var(--component-bg-color)' }}>
                  <p className="text-[13px] leading-relaxed font-light" style={{ color: 'var(--text-color)' }}>
                    <span className="text-yellow-500 font-bold block mb-1">⚠️ Note:</span>
                    "ข้อมูลและไอเดียการเทรดทั้งหมดนี้ เป็นเพียงกรณีศึกษาเพื่อนำไปต่อยอดเท่านั้น ไม่ใช่การชี้นำต้องลงทุน เพื่อนๆควรนำไปศึกษาทำการบ้านเพิ่มเติมเพื่อตัดสินใจด้วยตัวเอง ต้องไม่ลืมว่า <span className="text-yellow-500/80 font-medium">"ต้นทุนค่าเฉลี่ย, เงินสำรอง, การแบ่งไม้เข้าซื้อ, กลยุทธ์ รวมถึงการรับความเสี่ยง"</span> ของเราแต่ละคน <strong className="text-yellow-500/80 mx-1">**ไม่เท่ากัน**</strong> การลงทุนที่ดีควรยึดเป้าหมาย และหน้าตักพอร์ตของเราเป็นหลักเสมอครับ"
                  </p>
                </div>
              </div>

              {/* ฝั่งขวา: Tips 2 อัน */}
              <div className="flex flex-col gap-4 flex-1">
                <div className="bg-white/5 border-l-4 border-blue-500/50 p-3 rounded-r-lg flex-1"
                  style={{ background: 'var(--component-bg-color)' }}>
                  <p className="text-[13px] leading-relaxed font-light" style={{ color: 'var(--text-color)' }}>
                    <span className="text-blue-400 font-bold mb-1">🎨 Tips: </span>
                    "สีของกรอบหุ้นจะ <strong className="text-blue-400/90 mx-1">เปลี่ยนไปตามรอบการอัปเดต</strong> โดยหุ้นที่มีสีของกรอบเหมือนกัน หมายถึงหุ้นกลุ่มที่ถูกปรับปรุงข้อมูลพร้อมกันในรอบนั้นๆ เพื่อให้เพื่อนๆ สังเกตการเปลี่ยนแปลงล่าสุดได้ง่ายขึ้นครับ"
                  </p>
                </div>
                <div className="bg-white/5 border-l-4 border-green-500/50 p-3 rounded-r-lg flex-1"
                  style={{ background: 'var(--component-bg-color)' }}>
                  <p className="text-[13px] leading-relaxed font-light" style={{ color: 'var(--text-color)' }}>
                    <span className="text-green-400 font-bold mb-1">🔔 Telegram Alert (Free!): </span>
                    "สำหรับเพื่อนๆ ที่อยากเกาะติดพอร์ตการลงทุนให้ทันท่วงที <strong className="text-green-400 font-medium">ผมเปิดระบบแจ้งเตือนผ่าน Telegram ให้ใช้งานฟรี</strong> เพื่อให้เพื่อนๆ ไม่พลาดทุกไม้ที่ Guru มีการเคลื่อนไหวแบบ <strong className="text-green-400 font-medium">Real-time</strong> ครับ" สามารถคลิกที่โปรไฟล์บนขวาเพื่อลงทะเบียน Telegram ได้เลย
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
        {/* 🟢 กลุ่มปุ่มเครื่องมือของแอดมินโดยเฉพาะ (จัดเรียงใหม่ให้อยู่แถวเดียวกัน) */}
        {userInfo?.isAdmin && (
          <div className="flex flex-wrap justify-end items-center mb-6 gap-3 w-full border-b border-gray-700/50 pb-4">
            <button
              className="bg-red-400 text-black px-4 py-2 rounded-lg font-bold hover:bg-red-300 shadow-md transition-colors"
              onClick={() => setShowGuruModal(true)}
            >
              + สร้าง/แก้ไข Guru
            </button>
            <button
              className="bg-yellow-400 text-black px-4 py-2 rounded-lg font-bold hover:bg-yellow-300 shadow-md transition-colors"
              onClick={() => {
                setEditPortfolio(null);
                setShowCreateModal(true);
              }}
            >
              + สร้างพอร์ตใหม่
            </button>
            <button
              onClick={() => {
                // เปิด Split Modal สำหรับ Bulk Sync
                setPreviewData(null);
                setShowSyncModal(true);
              }}
              className="bg-purple-500 hover:bg-purple-400 text-white px-5 py-2 rounded-lg font-bold shadow-md transition-colors flex items-center gap-2"
            >
              <span>🔄</span> Sync ข้อมูลเข้าพอร์ตจารย์ Shay
            </button>
            <button
              onClick={handleOpenHistoryModal}
              className="bg-cyan-600 hover:bg-cyan-500 text-white px-4 py-2 rounded font-bold shadow-md transition-colors flex items-center justify-center gap-2"
            >
              📜 ดูประวัติการ Sync
            </button>
          </div>
        )}
      </div>
      <div className="mb-6 flex flex-col sm:flex-col items-start gap-3">
        <label className="text-sm font-bold" style={{ color: 'var(--secondary-text-color)' }}>
          เลือกดูบันทึกย้อนหลัง:
        </label>
        <div className="relative w-full sm:w-80">
          <select
            value={selectedDateKey}
            onChange={(e) => setSelectedDateKey(e.target.value)}
            className="w-full px-4 py-2.5 rounded-lg appearance-none cursor-pointer transition-all border border-gray-700 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 shadow-lg"
            style={{
              backgroundColor: 'var(--component-bg-color)',
              color: 'var(--text-color)',
              backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='white'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'right 12px center',
              backgroundSize: '16px'
            }}
          >
            {weeklyDates.map((item) => (
              <option key={item.timeCode} value={item.timeCode}>
                {item.message}
              </option>
            ))}
          </select>
        </div>
      </div>
      {/* Dropdown สำหรับเลือก Guru */}
      {/* <div className="relative mb-2">
        <button
          className="w-full sm:w-auto px-4 py-2 rounded flex justify-between items-center"
          style={{
            backgroundColor: 'var(--component-bg-color)',
            color: 'var(--text-color)',
            border: '1px solid var(--border-item-dark)',
          }}
          onClick={() => setDropdownOpen(!dropdownOpen)}
        >
          {gurus.find((guru) => guru.id === selectedGuruId)?.name || 'เลือก Guru'}
          <span className="ml-2">▼</span>
        </button> */}

      {/* <div
          className={`absolute mt-1 w-full sm:w-auto rounded shadow-lg z-10 ease-in-out transition-all duration-300 ${dropdownOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
            }`}
          style={{
            backgroundColor: 'var(--component-bg-color)',
            border: '1px solid var(--border-item-dark)',
            overflow: 'hidden',
          }}
        >
          {gurus.map((guru) => (
            <div
              key={guru.id}
              className="px-4 py-2 hover:bg-gray-700 cursor-pointer flex justify-between items-center"
              style={{
                backgroundColor: 'var(--component-bg-color)',
                color: 'var(--text-color)',
              }}
              onClick={() => {
                setSelectedGuruId(guru.id);
                setDropdownOpen(false);
              }}
            >
              <span>{guru.name}</span>
              {hasNewPortfolio(guru.id) && (
                <span
                  className="ml-2 px-2 py-1 text-xs font-bold rounded animate-pulse"
                  style={{
                    backgroundColor: 'var(--new-badge-bg)',
                    color: 'var(--new-badge-text)',
                  }}
                >
                  NEW
                </span>
              )}
            </div>
          ))}
        </div>
      </div> */}


      {/* Container หลักที่แบ่งครึ่งหน้า (2 items per row) */}
      {displayPortfolios.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {displayPortfolios.map((portfolio) => {
            // ตรวจสอบว่า lastUpdated อยู่ภายใน 24 ชั่วโมงหรือไม่
            const isNew = new Date().getTime() - new Date(portfolio.lastUpdated).getTime() <= 24 * 60 * 60 * 1000;
            const formatTime = (dateStr?: string) => {
              if (!dateStr) return '';
              return new Date(dateStr).toLocaleTimeString('th-TH', {
                hour: '2-digit',
                minute: '2-digit',
              }) + ' น.';
            };
            // 1. ตรวจสอบว่าเป็นพอร์ตใหม่ภายใน 24 ชม. หรือไม่
            const isNewIn24h = new Date().getTime() - new Date(portfolio.lastUpdated).getTime() <= 24 * 60 * 60 * 1000;

            // 2. เช็คว่าเป็นการสร้างใหม่ (CreatedAt ตรงกับ UpdatedAt) หรือเป็นการอัปเดต (ค่าไม่เท่ากัน)
            // ใช้ระดับนาทีในการเปรียบเทียบเพื่อป้องกันเศษวินาทีที่อาจคลาดเคลื่อนเล็กน้อย
            const isActuallyUpdated = portfolio.createdAt.substring(0, 16) !== portfolio.updatedAt.substring(0, 16);

            // ฟังก์ชัน Render UI ตามเงื่อนไข Guru
            const renderGuruContent = () => {
              const allHoldingsForColor = [...(portfolio.buys || []), ...(portfolio.sells || [])];


              // --- รูปแบบที่ 2: AP Guru (เช็คก่อนเหมือนเดิม) ---
              if (portfolio.guruId === 'eba14023-edb7-4fed-aa59-0dd2ae07de46') {
                return (
                  <div className="flex flex-col gap-4">
                    {/* ส่วนบน: รายการซื้อ (Buy) */}
                    <div className="flex-1">
                      <h4 className="text-lg font-bold mb-3 flex items-center gap-2  text-green-400" >
                        <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                        ซื้อ (Buy)
                      </h4>
                      <div className="space-y-2">
                        {portfolio.buys.length > 0 ? (
                          portfolio.buys.map((buy, i) => {
                            // --- เพิ่มบรรทัดนี้เพื่อคำนวณสี ---
                            const borderColor = getSequenceColor(buy.updatedAt, portfolio);

                            return (
                              <div
                                key={i}
                                className="p-3 border rounded-lg bg-[#1e1e24] flex flex-col gap-2 cursor-pointer hover:border-orange-500/50 transition-colors overflow-hidden"
                                style={{
                                  backgroundColor: 'var(--realtime-component-item-bg)',
                                  borderColor: borderColor,
                                  borderLeft: `4px solid ${borderColor}`,
                                }}
                                onMouseEnter={(e) => handleMouseEnter({ ...buy, action: 'buy' }, e)}
                                onClick={(e) => handleItemClick({ ...buy, action: 'buy' }, e)}
                                onMouseLeave={handleMouseLeave}
                              >
                                {/* แถวบน: Ticker และ Reason */}
                                <div className="flex justify-between items-center">
                                  <div className="flex flex-col min-w-[80px]">
                                    <span className="text-[18px] text-orange-500 font-black uppercase">{buy.ticker}</span>
                                    {buy.proportion && (
                                      <span className="text-[12px] text-gray-500 font-bold">สัดส่วน: {buy.proportion}%</span>
                                    )}
                                  </div>
                                  <div className="flex-1 px-4 min-w-0">
                                    <p className="text-sm font-medium line-clamp-3 break-all" style={{ color: 'var(--text-color)' }}>
                                      — {buy.reason || ""}
                                    </p>
                                  </div>
                                </div>

                                {/* แถวล่าง: ย้ายเวลามาไว้ตรงนี้ */}
                                <div className="flex justify-end border-t border-white/5 pt-1">
                                  <span className="text-[12px] text-gray-500  uppercase tracking-tight" style={{ color: 'var(--text-color)', opacity: 0.7 }}>
                                    อัปเดตเมื่อ: {formatTime(buy.updatedAt)}
                                  </span>
                                </div>
                              </div>
                            );
                          })
                        ) : (
                          <div className="text-gray-500 text-sm italic py-2">ไม่มีรายการซื้อ</div>
                        )}
                      </div>
                    </div>

                    {/* ส่วนล่าง: รายการขาย (Sell) */}
                    <div className="pt-2 border-t border-gray-700/50">
                      <h4 className="text-lg font-bold mb-3 flex items-center gap-2 text-red-400" >
                        <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                        ขาย (Sell)
                      </h4>
                      <div className="flex-1 flex-wrap gap-2">
                        {portfolio.sells.length > 0 ? (
                          portfolio.sells.map((sell, i) => {
                            const borderColor = getSequenceColor(sell.updatedAt, portfolio);
                            return (
                              <div
                                key={i}
                                className="px-3 py-2 border rounded-lg bg-[#1e1e24]/50 flex flex-col gap-1 group overflow-hidden shrink-0 cursor-pointer"
                                style={{
                                  backgroundColor: 'var(--realtime-component-item-bg)',
                                  borderColor: borderColor,
                                  borderLeft: `4px solid ${borderColor}`,
                                }}
                                onMouseEnter={(e) => handleMouseEnter({ ...sell, action: 'sell' }, e)}
                                onClick={(e) => handleItemClick({ ...sell, action: 'sell', batches: sell.batches }, e)}
                                onMouseLeave={handleMouseLeave}
                              >
                                {/* แถวบน: ข้อมูลการขาย */}
                                <div className="flex items-center w-full">
                                  <span className="text-[18px] text-red-500 font-bold uppercase group-hover:text-red-400 transition-colors shrink-0">
                                    🛒 {sell.ticker}
                                  </span>
                                  <div className="min-w-0 flex-1 ml-2 px-4 ">
                                    <span className="text-sm font-medium line-clamp-3 break-all" style={{ color: 'var(--text-color)' }}>
                                      — {sell.reason || ""}
                                    </span>
                                  </div>
                                  {sell.proportion && (
                                    <span className="ml-2 bg-red-500/10 px-1.5 py-0.5 rounded text-[14px] font-bold shrink-0">
                                      {sell.proportion}%
                                    </span>
                                  )}
                                </div>

                                {/* แถวล่าง: ย้ายเวลามาไว้ตรงนี้ */}
                                <div className="flex justify-end border-t border-white/5 pt-1">
                                  <span className="text-[12px] text-gray-500  uppercase tracking-tight" style={{ color: 'var(--text-color)', opacity: 0.7 }}>
                                    อัปเดตเมื่อ: {formatTime(sell.updatedAt)}
                                  </span>
                                </div>
                              </div>
                            );
                          })
                        ) : (
                          <div className="text-gray-400 text-center italic py-2 w-full text-sm">พอร์ตนี้ไม่มีการขายจ้า 🛒</div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              }

              // --- รูปแบบที่ 3: AD Guru (ย้ายมาเช็ค ID เฉพาะเจาะจงที่นี่) ---
              else if (portfolio.guruId === '2d51d1c5-e764-4503-9293-3a98c5d39123') {
                return (
                  <div className="flex flex-col gap-4">
                    {/* ส่วน Note: ปรับให้แสดง 2 บรรทัดใน Mobile */}
                    <div className="bg-blue-500/5 border border-blue-500/20 p-3 rounded-lg shadow-sm">
                      <div className="flex items-start gap-2">
                        <span className="text-blue-400 shrink-0">ℹ️</span>
                        <div className="flex flex-col flex-1">
                          <p className={`text-[12px] leading-relaxed text-gray-400 font-light ${!isAdDumNoteExpanded ? 'line-clamp-2 md:line-clamp-none' : ''}`}>
                            <span className="text-blue-400 font-bold mr-1">Note:</span>
                            พอร์ตนี้ไม่ได้แสดง "ค่าเฉลี่ยไม้ที่ได้" เพราะเนื่องจาก "แอ๊ดอั้ม" ใช้ M1 Finance Broker จะไม่ทราบค่าเฉลี่ยที่ได้ จนกว่าระบบจะ Random ซื้อหุ้นให้หลังจากตลาดเปิด 15 นาที ถ้าครึ่งหลังตลาด ก็จะซื้อให้ก่อนตลาดปิด 1 ชม. แต่จะมี "สรุปค่าเฉลี่ยทุกไม้" ที่โพสสมาชิก ชื่อ "Weekly Report" ทุกๆวันอาทิตย์
                          </p>
                          {/* ปุ่มดูเพิ่มเติม: แสดงเฉพาะใน Mobile (md:hidden) */}
                          <button
                            onClick={() => setIsAdDumNoteExpanded(!isAdDumNoteExpanded)}
                            className="md:hidden text-blue-400 text-[11px] font-bold mt-1 self-start"
                          >
                            {isAdDumNoteExpanded ? "...ดูน้อยลง" : "...ดูเพิ่มเติม"}
                          </button>
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-col gap-6">
                      {/* ส่วนกราฟและการซื้อ: ใน Mobile จะเรียงเป็นแนวตั้ง (flex-col) ใน Desktop เป็นแนวนอน (md:flex-row) */}
                      <div className="flex flex-col md:flex-row gap-4 md:gap-6 items-center md:items-start">

                        {/* Donut Chart: ปรับขนาดให้พอดี Mobile */}
                        {portfolio.buys && portfolio.buys.length > 0 && (
                          <div className="w-40 h-40 md:w-1/3 shrink-0 py-2 md:py-10">
                            <DonutChart data={portfolio.buys.map((buy) => ({ symbol: buy.ticker, proportion: buy.proportion || 0 }))} />
                          </div>
                        )}

                        <div className="flex-1 w-full">
                          <h4 className="text-lg font-bold mb-3 flex items-center gap-2  text-green-400" >
                            <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                            ซื้อ (Buy)
                          </h4>
                          <div className="space-y-2">
                            {portfolio.buys.length > 0 ? (
                              portfolio.buys.map((buy, i) => {
                                const borderColor = getSequenceColor(buy.updatedAt, portfolio);
                                return (
                                  <div
                                    key={i}
                                    className="p-3 border border-gray-700/50 rounded-lg flex flex-col sm:flex-row justify-between items-start sm:items-center cursor-pointer hover:border-orange-500/50 transition-colors overflow-hidden"
                                    style={{
                                      backgroundColor: 'var(--realtime-component-item-bg)',
                                      borderColor: borderColor,
                                      borderLeft: `4px solid ${borderColor}`,
                                    }}
                                    onMouseEnter={(e) => handleMouseEnter({ ...buy, action: 'buy' }, e)}
                                    onClick={(e) => handleItemClick({ ...buy, action: 'buy' }, e)}
                                    onMouseLeave={handleMouseLeave}
                                  >
                                    <div className="flex flex-col min-w-[80px] w-full sm:w-auto">
                                      <div className="flex justify-between sm:block">
                                        <div className="flex flex-col min-w-[80px]">
                                          <span className="text-[18px] text-orange-500 font-black uppercase leading-none">{buy.ticker}</span>
                                          {/* ย้ายสัดส่วนมาไว้ตรงนี้ เป็นสีเทา */}
                                          {buy.proportion && (
                                            <span className="text-[12px] text-gray-500 font-medium mt-1 ">สัดส่วน: {buy.proportion}%</span>
                                          )}
                                        </div>
                                        {buy.proportion && (
                                          <span className="text-[12px] text-cyan-400 font-bold sm:hidden">{buy.proportion}%</span>
                                        )}
                                      </div>
                                      <span className="text-[10px]  mt-1  uppercase tracking-tight" style={{ color: 'var(--text-color)' }}>
                                        อัปเดต: {formatTime(buy.updatedAt)}
                                      </span>
                                    </div>
                                    <div className="flex-1 sm:px-4 min-w-0 mt-2 sm:mt-0">
                                      <p className="text-sm font-medium line-clamp-3 break-all" style={{ color: 'var(--text-color)' }}>                                        — {buy.reason || ""}
                                      </p>
                                    </div>

                                  </div>
                                )
                              })
                            ) : (
                              <div className="text-gray-500 text-sm italic py-2 text-center">พอร์ตนี้ไม่มีการซื้อจ้า 🛒</div>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* ส่วนขาย (Sell): ปรับให้กระชับใน Mobile */}
                      <div className="pt-4 border-t border-gray-700/50">
                        <h4 className="text-lg font-bold mb-3 flex items-center gap-2 text-red-400" >
                          <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                          ขาย (Sell)
                        </h4>
                        <div className="grid grid-cols-1 gap-2 max-h-[250px] overflow-y-auto pr-1 ">
                          {portfolio.sells.length > 0 ? (
                            portfolio.sells.map((sell, i) => {
                              const borderColor = getSequenceColor(sell.updatedAt, portfolio);
                              return (
                                <div
                                  key={i}
                                  className="p-3 border rounded-lg flex flex-col gap-1 cursor-pointer"
                                  style={{
                                    backgroundColor: 'var(--realtime-component-item-bg)',
                                    borderColor: borderColor,
                                    borderLeft: `4px solid ${borderColor}`,
                                  }}
                                  onMouseEnter={(e) => handleMouseEnter({ ...sell, action: 'sell' }, e)}
                                  onMouseLeave={handleMouseLeave}
                                  onClick={(e) => handleItemClick({ ...sell, action: 'sell' }, e)}
                                >
                                  <div className="flex justify-between items-center">
                                    <span className="text-red-500 font-bold text-[16px] uppercase">🛒 {sell.ticker}</span>
                                    <span className="text-[12px] bg-red-500/10 text-red-400 px-2 py-0.5 rounded font-bold">
                                      {sell.proportion ? `${sell.proportion}%` : '100%'}
                                    </span>
                                  </div>
                                  <div className="flex justify-between items-end">
                                    <span className="text-sm font-medium line-clamp-3" style={{ color: 'var(--text-color)' }}>— {sell.reason}</span>
                                    <span className="text-[9px]  shrink-0" style={{ color: 'var(--text-color)' }}>อัปเดต: {formatTime(sell.updatedAt)}</span>
                                  </div>
                                </div>
                              )
                            })
                          ) : (
                            <div className="text-gray-400 text-center italic py-4 text-sm">พอร์ตนี้ไม่มีการขายจ้า 🛒</div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              }

              // --- รูปแบบที่ 1: AS Guru (กลายเป็น Default) ---
              else {
                return (
                  <div className="space-y-8">
                    {/* ส่วนซื้อ (Buy) */}
                    <div className="flex flex-col lg:flex-row gap-6 items-start">
                      <div className="w-full lg:w-1/4 shrink-0 p-4 ">
                        <DonutChart data={portfolio.buys.map((buy) => ({ symbol: buy.ticker, proportion: buy.proportion || 0 }))} />
                      </div>

                      <div className="flex-1 w-full">
                        <h4 className="text-lg font-bold mb-4 flex items-center gap-2 text-green-400">
                          <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                          ซื้อ (Buy)
                        </h4>

                        {portfolio.buys && portfolio.buys.length > 0 ? (
                          <>
                            {/* --- DESKTOP TABLE: แสดงเฉพาะจอ md ขึ้นไป --- */}
                            <div className="hidden md:block overflow-hidden rounded-xl border border-gray-700/50 shadow-sm bg-[#1e1e24]/50" style={{ backgroundColor: 'var(--component-bg-color)' }}>
                              <table className="w-full text-sm text-left table-fixed border-collapse">
                                <thead className="bg-[#1e1e24] text-cyan-400 font-bold border-b border-gray-700" style={{ backgroundColor: 'var(--component-bg-color)' }}>
                                  <tr>
                                    <th className="p-3 border-r border-gray-800/50 w-18">ชื่อหุ้น</th>
                                    <th className="p-3 border-r border-gray-800/50 w-12 text-center">ไม้</th>
                                    <th className="border-r border-gray-800/50 w-22 text-center">จำนวน(หุ้น)</th>
                                    <th className="p-3 border-r border-gray-800/50 w-28 text-center">ราคา(USD)</th>
                                    <th className="p-3">เหตุผล</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-800">
                                  {portfolio.buys.map((buy, i) => (
                                    <React.Fragment key={i}>
                                      {buy.batches.map((batch, idx) => {
                                        const borderColor = getSequenceColor(batch.createdAt, portfolio);
                                        return (
                                          <tr key={`${i}-${idx}`} className="hover:bg-white/5 transition-colors cursor-pointer"
                                            style={{ borderLeft: `4px solid ${borderColor}`, borderRight: `4px solid ${borderColor}` }}
                                            onMouseEnter={(e) => handleMouseEnter({ ...buy, action: 'buy', batches: buy.batches }, e)}
                                            onMouseLeave={handleMouseLeave}
                                            onClick={(e) => handleItemClick({ ...buy, action: 'buy', batches: buy.batches }, e)}
                                          >
                                            {idx === 0 && (
                                              <td rowSpan={buy.batches.length} className="p-2 border-r border-gray-800/50 bg-black/5 align-top">
                                                <div className="flex flex-col">
                                                  <span className="font-black text-yellow-500 text-base">{buy.ticker}</span>
                                                  <span className="text-[10px] " style={{ color: 'var(--text-color)' }}>เมื่อ{formatTime(batch.createdAt)}</span>
                                                </div>
                                              </td>
                                            )}
                                            <td className="p-3 border-r border-gray-800/50 text-center text-gray-400 font-bold bg-black/5 w-12">{idx + 1}</td>
                                            <td className="p-3 border-r border-gray-800/50 text-center font-medium w-22" style={{ color: 'var(--text-color)' }}>{batch.qty.toLocaleString()}</td>
                                            <td className="p-3 border-r border-gray-800/50 text-center  text-cyan-400 w-28">${batch.price}</td>
                                            {idx === 0 && (
                                              <td rowSpan={buy.batches.length} className="p-3 text-gray-400 align-top" style={{ color: 'var(--text-color)' }}>
                                                <div className="text-base leading-snug break-words line-clamp-3 font-medium">{buy.reason || "-"}</div>
                                              </td>
                                            )}
                                          </tr>
                                        );
                                      })}
                                    </React.Fragment>
                                  ))}
                                </tbody>
                              </table>
                            </div>

                            {/* --- MOBILE ITEMS: แสดงเฉพาะจอเล็ก --- */}
                            <div className="md:hidden space-y-3">
                              {portfolio.buys.map((buy, i) => (
                                <div key={i} className="bg-[#1e1e24] rounded-lg overflow-hidden border border-gray-700/50 shadow-md"
                                  onMouseEnter={(e) => handleMouseEnter({ ...buy, action: 'buy', batches: buy.batches }, e)}
                                  onMouseLeave={handleMouseLeave}
                                  onClick={(e) => handleItemClick({ ...buy, action: 'buy', batches: buy.batches }, e)}
                                  style={{ backgroundColor: 'var(--component-bg-color)' }}>
                                  <div className="p-3 bg-black/20 flex justify-between items-center border-b border-gray-800" style={{ backgroundColor: 'var(--component-bg-color)' }}>
                                    <span className="font-black text-yellow-500 text-lg">{buy.ticker}</span>
                                    <span className="text-[10px]  text-gray-400">อัปเดต: {formatTime(buy.batches[buy.batches.length - 1]?.createdAt)}</span>
                                  </div>
                                  <div className="p-3 space-y-2">
                                    {buy.batches.map((batch, idx) => {
                                      const borderColor = getSequenceColor(batch.createdAt, portfolio);
                                      return (
                                        <div key={idx} className="flex justify-between items-center bg-black/10 p-2 rounded border-l-4" style={{ borderColor: borderColor, backgroundColor: 'var(--component-bg-color)' }}>
                                          <span className="text-xs text-gray-400 font-bold">ไม้ {idx + 1}</span>
                                          <div className="flex gap-4">
                                            <span className="text-sm font-medium text-gray-200">{batch.qty.toLocaleString()} หุ้น</span>
                                            <span className="text-sm  text-cyan-400 font-bold">${batch.price}</span>
                                          </div>
                                        </div>
                                      );
                                    })}
                                    {buy.reason && (
                                      <div className="mt-2 pt-2 border-t border-gray-800/50 text-base text-[var(--text-color)] font-medium line-clamp-3">
                                        — {buy.reason}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </>
                        ) : (
                          <div className="text-gray-500 text-sm italic py-4 bg-black/10 rounded-lg text-center">พอร์ตนี้ไม่มีการซื้อจ้า 🛒</div>
                        )}
                      </div>
                    </div>

                    {/* ส่วนขาย (Sell) */}
                    <div>
                      <h4 className="text-lg font-bold mb-4 flex items-center gap-2 text-red-400">
                        <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                        ขาย (Sell)
                      </h4>

                      {portfolio.sells && portfolio.sells.length > 0 ? (
                        <>
                          {/* --- DESKTOP TABLE: ขาย --- */}
                          <div className="hidden md:block overflow-hidden rounded-xl border border-gray-700/50 shadow-sm bg-[#1e1e24]/50">
                            <table className="w-full text-sm text-left table-fixed border-collapse">
                              <thead className="bg-[#1e1e24] text-red-400 font-bold border-b border-gray-700" style={{ backgroundColor: 'var(--component-bg-color)' }}>
                                <tr>
                                  <th className="p-3 border-r border-gray-800/50 w-24">ชื่อหุ้น</th>
                                  <th className="p-3 border-r border-gray-800/50 w-12 text-center">ไม้</th>
                                  <th className="p-3 border-r border-gray-800/50 w-24 text-center">จำนวน</th>
                                  <th className="p-3 border-r border-gray-800/50 w-28 text-center">ราคา</th>
                                  <th className="p-3">เหตุผล</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-800 " style={{ backgroundColor: 'var(--component-bg-color)' }}>
                                {portfolio.sells.map((sell, i) => (
                                  <React.Fragment key={i}>
                                    {sell.batches.map((batch, idx) => {
                                      const borderColor = getSequenceColor(batch.createdAt, portfolio);
                                      return (
                                        <tr key={`${i}-${idx}`} className="hover:bg-red-500/5 transition-colors cursor-pointer"
                                          style={{ borderLeft: `4px solid ${borderColor}`, borderRight: `4px solid ${borderColor}` }}
                                          onMouseEnter={(e) => handleMouseEnter({ ...sell, action: 'sell', batches: sell.batches }, e)}
                                          onMouseLeave={handleMouseLeave}
                                          onClick={(e) => handleItemClick({ ...sell, action: 'sell', batches: sell.batches }, e)}
                                        >
                                          {idx === 0 && (
                                            <td rowSpan={sell.batches.length} className="p-2 border-r border-gray-800/50 bg-black/5 align-top">
                                              <div className="flex flex-col">
                                                <span className="font-black text-red-500 text-base">{sell.ticker}</span>
                                                <span className="text-[10px] text-gray-500 ">เมื่อ{formatTime(batch.createdAt)}</span>
                                              </div>
                                            </td>
                                          )}
                                          <td className="p-3 text-center text-gray-400 font-bold border-r border-gray-800/50 bg-black/5 w-12">{idx + 1}</td>
                                          <td className="p-3 border-r border-gray-800/50 text-center font-medium" style={{ color: 'var(--text-color)' }}>{batch.qty.toLocaleString()}</td>
                                          <td className="p-3 border-r border-gray-800/50 text-center  text-gray-300" style={{ color: 'var(--text-color)' }}>${batch.price}</td>
                                          {idx === 0 && (
                                            <td rowSpan={sell.batches.length} className="p-3 text-gray-400 align-top" style={{ color: 'var(--text-color)' }}>
                                              <div className="text-base line-clamp-3 font-medium">{sell.reason || "-"}</div>
                                            </td>
                                          )}
                                        </tr>
                                      );
                                    })}
                                  </React.Fragment>
                                ))}
                              </tbody>
                            </table>
                          </div>

                          {/* --- MOBILE ITEMS: ขาย --- */}
                          <div className="md:hidden space-y-3">
                            {portfolio.sells.map((sell, i) => (
                              <div key={i} className="bg-[#1e1e24] rounded-lg overflow-hidden border border-red-900/20 shadow-md" style={{ backgroundColor: 'var(--component-bg-color)' }}
                                onClick={(e) => handleItemClick({ ...sell, action: 'sell', batches: sell.batches }, e)}>
                                <div className="p-3 bg-red-500/10 flex justify-between items-center border-b border-red-900/20">
                                  <span className="font-black text-red-500 text-lg">🛒 {sell.ticker}</span>
                                  <span className="text-[10px]  text-gray-500">อัปเดต: {formatTime(sell.batches[sell.batches.length - 1]?.createdAt)}</span>
                                </div>
                                <div className="p-3 space-y-2">
                                  {sell.batches.map((batch, idx) => {
                                    const borderColor = getSequenceColor(batch.createdAt, portfolio);
                                    return (
                                      <div key={idx} className="flex justify-between items-center bg-black/10 p-2 rounded border-l-4" style={{ borderColor: borderColor }}>
                                        <span className="text-xs text-gray-400 font-bold">ไม้ {idx + 1}</span>
                                        <div className="flex gap-4">
                                          <span className="text-sm font-medium text-gray-300">{batch.qty.toLocaleString()} หุ้น</span>
                                          <span className="text-sm  text-gray-400">${batch.price}</span>
                                        </div>
                                      </div>
                                    );
                                  })}
                                  {sell.reason && (
                                    <div className="mt-2 pt-2 border-t border-gray-800/50 text-base text-[var(--text-color)] font-medium line-clamp-3">
                                      — {sell.reason}
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </>
                      ) : (
                        <div className="text-gray-500 text-sm italic py-4 bg-black/10 rounded-lg text-center">พอร์ตนี้ไม่มีการขายจ้า 🛒</div>
                      )}
                    </div>
                  </div>
                );
              }
            };

            return (
              <div
                key={portfolio.realtimeId}
                id={`realtime-card-${portfolio.realtimeId}`}
                className="relative p-6 rounded-xl shadow-lg transition-all"
                style={{
                  backgroundColor: 'var(--component-bg-color)', // ใช้สีพื้นหลังตาม Theme
                  // การ์ดที่ตรงกับ realtimeId จาก deep link แจ้งเตือน ให้ขอบเน้นไว้ให้สังเกตง่าย
                  border: portfolio.realtimeId === urlRealtimeId
                    ? '2px solid #22d3ee'
                    : '1px solid var(--border-item-dark)',
                  boxShadow: portfolio.realtimeId === urlRealtimeId ? '0 0 0 3px rgba(34,211,238,0.25)' : undefined,
                }}
              >
                {/* ปุ่มลบที่มุมบนซ้าย */}
                {userInfo?.isAdmin && (
                  <button
                    className="absolute top-0 left-0 w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-400 shadow-md"
                    onClick={() => {
                      // แสดงข้อความยืนยันก่อนลบ
                      toast((t) => (
                        <div>
                          <p>คุณต้องการลบพอร์ต <strong>{portfolio.portfolioName}</strong> ใช่หรือไม่?</p>
                          <div className="flex justify-end gap-2 mt-2">
                            <button
                              className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
                              onClick={async () => {
                                toast.dismiss(t.id); // ปิดข้อความยืนยัน
                                await handleDeleteRealtimePortfolio(portfolio.realtimeId); // ลบพอร์ต
                              }}
                            >
                              ใช่
                            </button>
                            <button
                              className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
                              onClick={() => toast.dismiss(t.id)} // ปิดข้อความยืนยัน
                            >
                              ยกเลิก
                            </button>
                          </div>
                        </div>
                      ));
                    }}
                  >
                    ✕
                  </button>
                )}
                {/* แสดงข้อความ NEW หาก lastUpdated อยู่ใน 24 ชั่วโมง */}
                {isNewIn24h && (
                  <div
                    className="absolute top-2 right-2 text-xs font-bold px-4 py-2 rounded animate-pulse shadow-lg"
                    style={{
                      backgroundColor: isActuallyUpdated ? '#f59e0b' : 'var(--new-badge-bg)', // ส้มอำพัน (Amber) สำหรับ UPDATE, เขียวสำหรับ NEW
                      color: isActuallyUpdated ? '#000' : 'var(--text-color)', // สีตัวอักษรดำเพื่อให้ตัดกับพื้นส้ม
                    }}
                  >
                    {isActuallyUpdated ? 'UPDATE' : 'NEW'}
                  </div>
                )}
                {/* Header ของแต่ละ Item */}
                <div className='flex flex-row justify-between'>
                  <div className="mb-4">
                    <h3
                      className={`text-xl font-bold leading-tight ${portfolio.guruId === '5f8adbab-f7ed-4564-9c18-43e81f74c244'
                        ? 'text-yellow-400'
                        : portfolio.guruId === '2d51d1c5-e764-4503-9293-3a98c5d39123'
                          ? 'text-green-400'
                          : portfolio.guruId === 'eba14023-edb7-4fed-aa59-0dd2ae07de46'
                            ? 'text-orange-400'
                            : 'text-blue-400'
                        }`}
                    >
                      {portfolio.portfolioName}
                      <p className="text-sm text-gray-400">
                        {/* ค้นหาชื่อกูรูที่ตรงกับ portfolio.guruId */}
                        {gurus.find((guru) => guru.id === portfolio.guruId)?.name || 'ไม่ทราบชื่อกูรู'}
                      </p>
                    </h3>
                    <p className={`text-[10px]  mt-1 uppercase ${portfolio.guruId === '5f8adbab-f7ed-4564-9c18-43e81f74c244'
                      ? 'text-yellow-600'
                      : portfolio.guruId === '2d51d1c5-e764-4503-9293-3a98c5d39123'
                        ? 'text-green-600'
                        : portfolio.guruId === 'eba14023-edb7-4fed-aa59-0dd2ae07de46'
                          ? 'text-orange-600'
                          : 'text-blue-600'
                      }`}>
                      อัปเดตล่าสุด: {new Date(portfolio.lastUpdated).toLocaleString()}
                    </p>
                  </div>
                  <div className="pr-12">
                    {/* เดิมปุ่ม "ส่งไปยัง Telegram" เดี่ยว ๆ ยิงตรง legacy broadcast — comment ไว้เผื่อ rollback
                    {userInfo?.isAdmin && !portfolio.telegramAnnounceAt && (
                      <button
                        className="mr-4 bg-blue-500 text-white px-3 py-1 rounded font-bold hover:bg-blue-400"
                        onClick={() => !portfolio.telegramAnnounceAt && handleSendToTelegram(portfolio.realtimeId)}
                        disabled={!!portfolio.telegramAnnounceAt}
                      >
                        {portfolio.telegramAnnounceAt ? 'ส่งแล้ว' : 'ส่งไปยัง Telegram'}
                      </button>
                    )}
                    */}
                    {userInfo?.isAdmin && (
                      <RealtimeNotifyButton
                        realtimeId={portfolio.realtimeId}
                        portfolioName={portfolio.portfolioName}
                        inAppAnnounceAt={portfolio.inAppAnnounceAt}
                        webPushAnnounceAt={portfolio.webPushAnnounceAt}
                        telegramAnnounceAt={portfolio.telegramAnnounceAt}
                        onSent={(channels) => {
                          // อัปเดต state ในหน้าเองทันที ไม่ต้องรอโหลดรายการใหม่จาก server
                          setRealtimePortfolios((prev) =>
                            prev.map((p) => {
                              if (p.realtimeId !== portfolio.realtimeId) return p;
                              const now = new Date().toISOString();
                              const patch: Partial<RealtimePortfolio> = {};
                              if (channels.includes('IN_APP')) patch.inAppAnnounceAt = now;
                              if (channels.includes('WEB_PUSH')) patch.webPushAnnounceAt = now;
                              if (channels.includes('TELEGRAM')) patch.telegramAnnounceAt = now;
                              return { ...p, ...patch };
                            })
                          );
                        }}
                      />
                    )}
                    {/* ✅ 2. เพิ่มปุ่ม Sync พอร์ตแอดมิน (แสดงเฉพาะพอร์ตของจารย์เชย์/Guru AS) */}
                    {/* {userInfo?.isAdmin &&
                      portfolio.guruId !== 'eba14023-edb7-4fed-aa59-0dd2ae07de46' && // ไม่ใช่ AP
                      portfolio.guruId !== '2d51d1c5-e764-4503-9293-3a98c5d39123' && // ไม่ใช่ แอดอั้ม
                      (
                        (() => {
                          // สร้าง Key สำหรับเช็คว่าพอร์ตนี้ในวันที่นี้เคย Sync ไปหรือยัง
                          const currentKey = `${portfolio.portfolioId || ''}_${portfolio.dateKey || ''}`;
                          const isAlreadySynced = syncedPortfolioKeys.includes(currentKey);

                          return (
                            <button
                              className={`px-3 py-1 mr-2 rounded font-bold w-full sm:w-auto shadow-md transition-colors ${isAlreadySynced
                                  ? 'bg-gray-600 text-gray-400 cursor-not-allowed opacity-60'
                                  : 'bg-purple-500 text-white hover:bg-purple-400'
                                }`}
                              disabled={isAlreadySynced}
                              onClick={() => {
                                if (!isAlreadySynced) {
                                  setSyncTargetPortfolio(portfolio);
                                  setShowSyncModal(true);
                                }
                              }}
                            >
                              {isAlreadySynced ? '✅ Sync เรียบร้อยแล้ว' : '🔄 Sync เข้าพอร์ตแอดมิน'}
                            </button>
                          );
                        })()
                      )} */}

                    {userInfo?.isAdmin && (

                      <button
                        className="bg-yellow-400 text-black px-3 py-1 rounded font-bold hover:bg-yellow-300 w-full sm:w-auto"
                        onClick={() => {
                          setEditPortfolio(portfolio); // ส่งข้อมูลพอร์ตที่ต้องการแก้ไข
                          setShowCreateModal(true); // เปิด modal
                        }}
                      >
                        แก้ไขข้อมูล
                      </button>
                    )}
                  </div>
                </div>
                {/* เรียกใช้ฟังก์ชันที่แยกตามเงื่อนไข */}
                {renderGuruContent()}
              </div>
            );
          })}
        </div>
      ) : (
        <div
          className="flex items-center justify-center h-[calc(100vh-400px)] text-center italic"
          style={{
            color: 'var(--text-color)',
          }}
        >
          ยังไม่มีอะไรอัพเดต
        </div>
      )}
      {/* ========================================== */}
      {/* Modal แสดงประวัติการ Sync (Export Marks) */}
      {/* ========================================== */}
      {showHistoryModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[9999] flex items-center justify-center p-4">
          <div className="bg-[#1e1e24] border border-cyan-500/50 rounded-2xl w-full max-w-3xl shadow-2xl flex flex-col max-h-[85vh]">

            {/* Header */}
            <div className="flex justify-between items-center p-4 border-b border-gray-700/50">
              <h3 className="text-xl font-bold text-cyan-400 flex items-center gap-2">
                <span>📜</span> ประวัติการ Sync เข้าพอร์ตแอดมิน (Export History)
              </h3>
              <button
                onClick={() => setShowHistoryModal(false)}
                className="text-gray-400 hover:text-white font-bold text-lg"
              >
                ✕
              </button>
            </div>

            {/* Body / Table List */}
            <div className="p-4 overflow-y-auto flex-1 custom-scrollbar space-y-3">
              {exportHistoryList.length > 0 ? (
                exportHistoryList.map((item: any, idx: number) => {
                  const completedMark = item.marks?.find((m: any) => m.type === 'UPLOAD_COMPLETED');
                  return (
                    <div key={idx} className="bg-black/30 border border-gray-700/50 p-3.5 rounded-xl space-y-2">
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="font-bold text-white text-base flex items-center gap-2">
                            <span>{item.portfolioName || item.portfolioId}</span>
                            <span className="text-xs text-gray-400 font-normal">({item.guruName})</span>
                          </div>
                          <div className="text-xs text-gray-400 mt-0.5">
                            วันที่: <strong className="text-cyan-400">
                              {(() => {
                                // ฟังก์ชันแปลง Format วันที่จาก YYYYMMDD เป็น DD/MM/YYYY
                                const formatDate = (dateStr: string) => {
                                  if (!dateStr || dateStr.length !== 8) return dateStr;
                                  const year = dateStr.substring(0, 4);
                                  const month = dateStr.substring(4, 6);
                                  const day = dateStr.substring(6, 8);
                                  return `${day}/${month}/${year}`;
                                };

                                const formattedFrom = formatDate(item.fromDate);
                                const formattedTo = formatDate(item.toDate);

                                // ถ้าจากและถึงเป็นวันเดียวกัน ให้โชว์แค่อันเดียวพอ
                                if (item.fromDate === item.toDate) {
                                  return formattedFrom;
                                }
                                return `${formattedFrom} ถึง ${formattedTo}`;
                              })()}
                            </strong> | จำนวนแถว: {item.rowCount} รายการ
                          </div>
                        </div>
                        <div>
                          {completedMark ? (
                            <span className="bg-green-500/10 text-green-400 border border-green-500/30 text-xs px-2.5 py-1 rounded-full font-bold">
                              ✅ Sync สำเร็จ
                            </span>
                          ) : (
                            <span className="bg-gray-700 text-gray-300 text-xs px-2.5 py-1 rounded-full font-bold">
                              ⏳ ยังไม่มีการยืนยัน
                            </span>
                          )}
                        </div>
                      </div>

                      {/* แสดงรายละเอียด Mark ถ้ามี */}
                      {completedMark && (
                        <div className="bg-gray-800/40 p-2 rounded-lg text-xs space-y-1 border border-gray-700/30">
                          <div className="text-gray-300">
                            <strong>หมายเหตุ:</strong> {completedMark.remark || '-'}
                          </div>
                          {completedMark.metadata?.importJobId && (
                            <div className="text-gray-500 font-mono text-[11px]">
                              Import Job ID: {completedMark.metadata.importJobId}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })
              ) : (
                <div className="text-center py-12 text-gray-400 italic">
                  ยังไม่มีประวัติการ Sync ข้อมูล
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-gray-700/50 flex justify-end bg-black/10 rounded-b-2xl">
              <button
                onClick={() => setShowHistoryModal(false)}
                className="px-5 py-2 bg-gray-600 hover:bg-gray-500 text-white text-sm font-bold rounded-lg transition-colors"
              >
                ปิดหน้าต่าง
              </button>
            </div>

          </div>
        </div>
      )}
      {/* ========================================== */}
      {/* Modal สำหรับยืนยันการ Sync เข้า Admin Portfolio */}
      {/* ========================================== */}
      {/* Split Modal: Bulk Sync & History */}
      {/* ========================================== */}
      {showSyncModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[9999] flex items-center justify-center p-2 sm:p-4">
          <div className="bg-[#1e1e24] border border-purple-500/50 rounded-2xl w-full max-w-6xl shadow-2xl flex flex-col md:flex-row h-full max-h-[90vh] overflow-hidden" style={{ backgroundColor: 'var(--component-bg-color)' }}>

            {/* ⬅️ ด้านซ้าย: ประวัติ History (แคบกว่า) */}
            <div className="w-full md:w-1/3 border-b md:border-b-0 md:border-r border-gray-700/50 flex flex-col h-[40vh] md:h-full bg-black/10">
              <div className="p-4 border-b border-gray-700/50">
                <h3 className="text-lg font-bold text-cyan-400 flex items-center gap-2">
                  <span>📜</span> ประวัติการ Sync ย้อนหลัง
                </h3>
              </div>
              <div className="p-4 overflow-y-auto flex-1 custom-scrollbar space-y-3">
                {exportHistoryList.length > 0 ? (
                  exportHistoryList.map((item: any, idx: number) => {
                    // หาป้ายที่บอกว่าทำสำเร็จแล้ว
                    const completedMark = item.marks?.find((m: any) => m.type === 'UPLOAD_COMPLETED');

                    // ฟังก์ชันแปลง Format วันที่จาก YYYYMMDD เป็น DD/MM/YYYY
                    const formatDate = (dateStr: string) => {
                      if (!dateStr || dateStr.length !== 8) return dateStr;
                      const year = dateStr.substring(0, 4);
                      const month = dateStr.substring(4, 6);
                      const day = dateStr.substring(6, 8);
                      return `${day}/${month}/${year}`;
                    };

                    const formattedFrom = formatDate(item.fromDate);
                    const formattedTo = formatDate(item.toDate);

                    const isLoadingDetail = loadingHistorySet.has(idx);
                    const detailRows = historyDetailsMap[idx];

                    return (
                      <div key={idx} className="bg-black/30 border border-gray-700/50 p-3 rounded-xl space-y-1.5">
                        <div className="flex justify-between items-start">
                          <span className="font-bold text-white text-sm">{item.portfolioName || item.portfolioId}</span>
                          {completedMark ? (
                            <span className="text-[10px] bg-green-500/20 text-green-400 px-2 py-0.5 rounded font-bold border border-green-500/30">✅ สำเร็จ</span>
                          ) : (
                            <span className="text-[10px] bg-gray-700 text-gray-300 px-2 py-0.5 rounded font-bold border border-gray-600/50">⏳ รอการยืนยัน</span>
                          )}
                        </div>

                        <div className="text-[11px] text-gray-400 mt-1">
                          วันที่: <strong className="text-cyan-400">
                            {/* ถ้าจากและถึงเป็นวันเดียวกัน ให้โชว์แค่อันเดียว */}
                            {item.fromDate === item.toDate ? formattedFrom : `${formattedFrom} ถึง ${formattedTo}`}
                          </strong>
                        </div>

                        <div className="text-[11px] text-gray-500">
                          จำนวนแถว: {item.rowCount} รายการ
                        </div>

                        {/* 🌟 แสดงรายละเอียดหุ้นที่ซื้อ-ขาย (โหลดอัตโนมัติจาก Preview API ตามช่วงวันที่) */}
                        <div className="mt-1.5 pt-1.5 border-t border-gray-700/50 space-y-1">
                          {isLoadingDetail ? (
                            <div className="text-[11px] text-gray-500 italic">กำลังโหลดรายละเอียด...</div>
                          ) : detailRows && detailRows.length > 0 ? (
                            detailRows.map((row: any, rIdx: number) => (
                              <div key={rIdx} className="flex items-center justify-between text-[11px] bg-black/20 rounded px-2 py-1">
                                <div className="flex items-center gap-1.5">
                                  <span className={`font-black ${row.action === 'BUY' ? 'text-green-500' : 'text-red-500'}`}>
                                    {row.ticker?.toUpperCase()}
                                  </span>
                                  <span className={`text-[9px] px-1 rounded ${row.action === 'BUY' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                                    {row.action === 'BUY' ? 'ซื้อ' : 'ขาย'}
                                  </span>
                                </div>
                                <span className="text-gray-300">{row.qty?.toLocaleString()} หุ้น @ {row.price}</span>
                              </div>
                            ))
                          ) : detailRows ? (
                            <div className="text-[11px] text-gray-500 italic">ไม่พบรายละเอียดรายการ</div>
                          ) : null}
                        </div>

                        {/* 🌟 แสดง Import Job ID (ถ้าพอร์ตนั้น Sync สำเร็จและมี Job ID กลับมา) */}
                        {completedMark?.metadata?.importJobId && (
                          <div className="text-[10px] text-gray-500 font-mono mt-2 pt-1.5 border-t border-gray-700/50">
                            Job ID: {completedMark.metadata.importJobId}
                          </div>
                        )}
                      </div>
                    );
                  })
                ) : (
                  <div className="text-center py-10 text-gray-500 text-xs italic">ไม่มีประวัติการ Sync</div>
                )}
              </div>
            </div>

            {/* ➡️ ด้านขวา: ฟอร์มตั้งค่า และ พรีวิว (กว้างกว่า) */}
            <div className="w-full md:w-2/3 flex flex-col h-[50vh] md:h-full">
              <div className="flex justify-between items-center p-4 border-b border-gray-700/50">

                <h3 className="text-lg font-bold text-purple-400 flex items-center gap-2">
                  <span>🔄 จัดการ Sync ข้อมูลแบบกลุ่ม</span>

                </h3>



                <button onClick={() => setShowSyncModal(false)} className="text-gray-400 hover:text-white font-bold text-lg">✕</button>
              </div>
             
              <div className="p-4 overflow-y-auto flex-1 custom-scrollbar space-y-4">

                {/* แผงตั้งค่าฟอร์ม */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 bg-black/20 p-4 rounded-xl border border-gray-700/30">
                  <div>
                    <label className="text-xs font-bold text-gray-400 mb-1 block">1. เลือก Guru ต้นทาง</label>
                    <select
                      value={syncForm.guruId}
                      disabled
                      onChange={(e) => setSyncForm({ ...syncForm, guruId: e.target.value, sourcePortfolioId: '' })}
                      className="w-full bg-[#15151a] border border-gray-700 text-white text-xs rounded-lg px-3 py-2 outline-none focus:border-purple-500"
                    >
                      <option value="" disabled>-- เลือกกูรู --</option>
                      {gurus.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
                    </select>
                  </div>

                  <div>
                    <label className="text-xs font-bold text-gray-400 mb-1 block">2. เลือกพอร์ตต้นทาง</label>
                    <select
                      value={syncForm.sourcePortfolioId}
                      onChange={(e) => {
                        const newSourceId = e.target.value;
                        setSyncForm({ ...syncForm, sourcePortfolioId: newSourceId });

                        // ✅ ผูกพอร์ตต้นทาง <-> พอร์ตแอดมินปลายทางอัตโนมัติ
                        // "Family Portfolio" -> พอร์ตหลัก เสมอ, "Growth Portfolio" -> พอร์ตรอง เสมอ
                        const sourceName = (gurus.find(g => g.id === syncForm.guruId)
                          ?.portfolios?.find(p => p.id === newSourceId)?.name || '').toLowerCase();

                        let matchedAdminPortfolio;
                        if (sourceName.includes('growth')) {
                          matchedAdminPortfolio = adminShayPortfolios.find(p => (p.name || '').toLowerCase().includes('พอร์ตรอง'));
                        } else if (sourceName.includes('family')) {
                          matchedAdminPortfolio = adminShayPortfolios.find(p => !(p.name || '').toLowerCase().includes('พอร์ตรอง'));
                        }

                        if (matchedAdminPortfolio) {
                          setSelectedAdminShayPortfolioId(matchedAdminPortfolio.adminPortfolioId);
                        }
                      }}
                      disabled={!syncForm.guruId}
                      className="w-full bg-[#15151a] border border-gray-700 text-white text-xs rounded-lg px-3 py-2 outline-none focus:border-purple-500 disabled:opacity-50"
                    >
                      <option value="" disabled>-- เลือกพอร์ตต้นทาง --</option>
                      {gurus.find(g => g.id === syncForm.guruId)?.portfolios?.map(p => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                      ))}
                    </select>
                  </div>

                  <div className="flex gap-2">
                    <div className="flex-1">
                      <label className="text-xs font-bold text-gray-400 mb-1 block">3. วันที่เริ่มต้น</label>
                      <input type="date" value={syncForm.fromDate} onChange={e => setSyncForm({ ...syncForm, fromDate: e.target.value })} className="w-full bg-[#15151a] border border-gray-700 text-white text-xs rounded-lg px-3 py-2 outline-none" />
                    </div>
                    <div className="flex-1">
                      <label className="text-xs font-bold text-gray-400 mb-1 block">ถึงวันที่</label>
                      <input type="date" value={syncForm.toDate} onChange={e => setSyncForm({ ...syncForm, toDate: e.target.value })} className="w-full bg-[#15151a] border border-gray-700 text-white text-xs rounded-lg px-3 py-2 outline-none" />
                    </div>
                  </div>

                  <div>
                    <label className="text-xs font-bold text-purple-400 mb-1 block">4. พอร์ตแอดมินปลายทาง</label>
                    <select
                      value={selectedAdminShayPortfolioId}
                      onChange={(e) => setSelectedAdminShayPortfolioId(e.target.value)}
                      className="w-full bg-purple-900/20 border border-purple-500/50 text-white text-xs rounded-lg px-3 py-2 outline-none focus:border-purple-400"
                    >
                      <option value="" disabled>-- เลือกพอร์ตปลายทาง --</option>
                      {adminShayPortfolios.map(port => <option key={port.adminPortfolioId} value={port.adminPortfolioId}>{port.name}</option>)}
                    </select>
                  </div>
                </div>

                {/* ปุ่ม Preview */}
                {!previewData && (
                  <button onClick={handlePreviewSync} className="w-full py-2.5 bg-blue-500 hover:bg-blue-400 text-white text-sm font-bold rounded-lg shadow-lg transition-colors">
                    🔍 ดึงข้อมูลและตรวจสอบ (Preview)
                  </button>
                )}

                {/* โซนแสดงผลพรีวิว (เอาโค้ดตาราง Before-After มาใส่ได้เลย) */}
                {previewData && (
                  <div className="animate-fadeIn mt-4 border-t border-gray-700/50 pt-4">
                    <h4 className="text-yellow-400 font-bold mb-3 text-sm flex items-center gap-2">
                      <span>📊</span> ผลการจำลองการ Sync
                    </h4>

                    {/* ตรวจจับ Error */}
                    {((previewData.errors && previewData.errors.length > 0) || (previewData.data?.errors && previewData.data.errors.length > 0)) && (
                      <div className="bg-red-500/10 border border-red-500/30 p-3 rounded-lg mb-4 text-xs">
                        <strong className="text-red-400 block mb-1">🚨 ตรวจพบปัญหา:</strong>
                        {(previewData.errors || previewData.data?.errors).map((err: any, i: number) => (
                          <div key={i} className="text-gray-300">- [{err.code}] {err.ticker}: {err.message}</div>
                        ))}
                      </div>
                    )}

                    {/* ตารางโชว์ Changes (ของเดิม) */}
                    {(previewData.data?.changes?.length > 0 || previewData.changes?.length > 0) && (
                      <div className="space-y-2 max-h-[250px] overflow-y-auto pr-2 custom-scrollbar">
                        {(previewData.data?.changes || previewData.changes).map((change: any, idx: number) => (
                          <div key={idx} className="bg-black/30 border border-gray-700/50 p-2.5 rounded-lg flex justify-between items-center text-xs">
                            <div className="flex items-center gap-2 w-1/3">
                              <span className={`font-black text-sm ${change.changeType === 'BUY' ? 'text-green-500' : 'text-red-500'}`}>{change.ticker}</span>
                              <span className="text-gray-400 bg-gray-800 px-1.5 rounded">{change.changeType}</span>
                            </div>
                            <div className="text-right flex items-center justify-end gap-2 w-2/3">
                              <span className="text-gray-500">{change.before?.quantity || 0}</span>
                              <span className="text-gray-600">➔</span>
                              <span className="text-white font-bold">{change.after?.quantity || 0} หุ้น</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* ============================================== */}
                    {/* 🌟 ส่วนที่เพิ่มใหม่: สรุปจำนวนและต้นทุนสุดท้าย */}
                    {/* ============================================== */}
                    {(previewData.data?.changes?.length > 0 || previewData.changes?.length > 0) && (
                      <div className="mt-4 pt-4 border-t border-gray-700/50">
                        <h4 className="text-cyan-400 font-bold mb-3 text-sm flex items-center gap-2">
                          <span>📋</span> สรุปพอร์ตหลัง Sync (Final Positions)
                        </h4>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                          {(() => {
                            // คำนวณหาสถานะสุดท้ายของหุ้นแต่ละตัว (เอาค่า after ของ array ตัวล่าสุด)
                            const changesList = previewData.data?.changes || previewData.changes || [];
                            const finalPositions = changesList.reduce((acc: any, curr: any) => {
                              // อัปเดตทับไปเรื่อยๆ ตัวสุดท้ายจะเป็นสถานะล่าสุด
                              acc[curr.ticker] = curr.after || { quantity: 0, averageCost: 0 };
                              return acc;
                            }, {});

                            return Object.entries(finalPositions).map(([ticker, state]: [string, any]) => (
                              <div key={ticker} className="bg-cyan-500/10 border border-cyan-500/20 p-3 rounded-lg flex flex-col items-center text-center shadow-sm">
                                <span className="text-cyan-400 font-black text-lg">{ticker}</span>
                                <span className="text-white font-bold text-sm mt-1">{state.quantity.toLocaleString()} หุ้น</span>
                                <span className="text-gray-400 text-xs mt-0.5">
                                  ทุนเฉลี่ย: <strong className="text-gray-300">${(state.averageCost || 0).toFixed(2)}</strong>
                                </span>
                              </div>
                            ));
                          })()}
                        </div>
                      </div>
                    )}
                    {/* ============================================== */}

                  </div>
                )}
              </div>

              {/* Footer Buttons */}
              <div className="p-4 border-t border-gray-700/50 flex justify-end gap-3 bg-black/10 rounded-br-2xl">
                <button
                  onClick={() => {
                    setPreviewData(null);
                    setShowSyncModal(false);
                  }
                  }
                  className="px-4 py-2 bg-gray-600 hover:bg-gray-500 text-white text-xs font-bold rounded-lg transition-colors">
                  {previewData ? '← ย้อนกลับ / แก้ไข' : 'ยกเลิก'}
                </button>
                {previewData && (
                  <button
                    onClick={handleCommitSync}
                    disabled={previewData.summary?.errorCount > 0}
                    className="px-5 py-2 bg-green-500 hover:bg-green-600 text-black text-xs font-bold rounded-lg shadow-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    🚀 ยืนยันบันทึก (Commit)
                  </button>
                )}
              </div>
            </div>

          </div>
        </div>
      )}

      <AddEditPortfolioRealtimeModal
        open={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          setEditPortfolio(null); // รีเซ็ต editPortfolio เมื่อปิด modal
        }}
        onCreated={() => window.location.reload()}
        gurus={gurus}
        onCreateRealtimePortfolio={handleCreateRealtimePortfolio}
        editPortfolio={editPortfolio || null} // เพิ่มค่าเริ่มต้น
        dateKey={selectedDateKey}
      />
      <GuruModal
        open={showGuruModal}
        onClose={() => setShowGuruModal(false)}
        gurus={gurus} // ส่งข้อมูลกูรูที่ดึงมาจาก API
        setGurus={setGurus} // ส่งฟังก์ชันสำหรับอัปเดตลิสต์กูรู
        onDeleteGuru={handleDeleteGuru} // ส่งฟังก์ชันสำหรับลบ Guru
        onCreateGuru={handleCreateGuru} // ส่งฟังก์ชันสร้าง Guru
        onCreatePortfolio={handleCreatePortfolio} // ส่งฟังก์ชันสร้าง Portfolio สำหรับ Guru
        onDeletePortfolio={handleDeletePortfolio} // ส่งฟังก์ชันลบ Portfolio สำหรับ Guru
      />
      <LayoutPopup
        open={showLayoutPopup}
        portfolios={[]}
        onClose={() => setShowLayoutPopup(false)}
        onReordered={() => window.location.reload()}
      />
      {hoverData && (
        <div
          className={`
      fixed z-[9999] p-5 rounded-2xl shadow-2xl border border-gray-700 
      bg-[#1e1e24] animate-fadeIn transition-all duration-300
      /* Mobile: จัดให้อยู่ตรงกลางหน้าจอเสมอ */
      max-md:top-1/2 max-md:left-1/2 max-md:-translate-x-1/2 max-md:-translate-y-1/2 max-md:w-[90vw] max-md:max-w-[350px]
      /* Desktop: เด้งตามตำแหน่งเมาส์เหมือนเดิม */
      md:w-80 md:absolute
    `}
          onMouseEnter={handlePopupMouseEnter}
          onMouseLeave={handleMouseLeave}
          style={{
            backgroundColor: 'var(--realtime-component-item-bg)',
            // ใช้ตำแหน่งเมาส์เฉพาะบน Desktop (จอใหญ่กว่า md)
            top: window.innerWidth >= 768 ? Math.max(10, mousePos.y - 200) : undefined,
            left: window.innerWidth >= 768
              ? (mousePos.x > window.innerWidth * 0.6 ? mousePos.x - 340 : mousePos.x + 20)
              : undefined,
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.7)'
          }}
        >
          {/* ปุ่มปิดเฉพาะใน Mobile เพื่อความสะดวก */}
          <button
            onClick={() => setHoverData(null)}
            className="md:hidden absolute -top-2 -right-2 w-8 h-8 bg-red-500 text-white rounded-full shadow-lg font-bold"
          >
            ✕
          </button>

          <div className="flex flex-col gap-3">
            {/* Header */}
            <div className="flex justify-between items-center border-b border-gray-700/50 pb-3 mb-1">
              <div className="flex items-center gap-2">
                <span className={`text-xl font-black ${hoverData.action === 'buy' ? 'text-green-500' : 'text-red-500'}`}>
                  {hoverData.ticker}
                </span>
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded uppercase tracking-wider ${hoverData.action === 'buy' ? 'bg-green-500/10 text-green-400' : 'bg-red-500/10 text-red-400'}`}>
                  {hoverData.action === 'buy' ? 'ซื้อ' : 'ขาย'}
                </span>
              </div>
            </div>

            {/* Case 1: Batches (รูปแบบ AS/AP) */}
            {hoverData.batches && hoverData.batches.length > 0 ? (
              <div className="space-y-1.5 my-1">
                <div className="text-[12px] text-gray-500 font-bold uppercase mb-1 tracking-tight" style={{ color: 'var(--text-color)' }}>
                  รายละเอียดราคา:
                </div>
                {hoverData.batches.map((b: any, idx: number) => (
                  <div key={idx} className="flex justify-between text-xs bg-black/30 p-2 rounded-lg border border-white/5" style={{ backgroundColor: 'var(--head-realtime)' }}>
                    <span className="text-gray-400 text-[12px]" style={{ color: 'var(--text-color)' }}>ไม้ {idx + 1}:</span>
                    <span className={`${hoverData.action === 'buy' ? 'text-cyan-400' : 'text-red-300'}  font-bold`}>
                      ${b.price} <span className="text-[10px] text-gray-500 font-normal" style={{ color: 'var(--text-color)' }}>({b.qty.toLocaleString()} หุ้น)</span>
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              /* Case 2: Proportion (รูปแบบแอดอั้ม) */
              hoverData.proportion && (
                <div className="text-base font-bold py-2 flex justify-between items-center bg-black/20 px-3 rounded-lg">
                  <span style={{ color: 'var(--text-color)' }}>สัดส่วนในไม้:</span>
                  <span className="text-cyan-400 text-lg">{hoverData.proportion}%</span>
                </div>
              )
            )}

            {/* Reason Section */}
            <div className="border-t border-gray-700/50 pt-3 mt-1">
              <span className="text-[12px] text-gray-500 font-bold block mb-2" style={{ color: 'var(--text-color)' }}>
                เหตุผลการเทรด:
              </span>
              <div className="text-[14px] leading-relaxed whitespace-pre-wrap break-words max-h-[30vh] overflow-y-auto pr-1 custom-scrollbar"
                style={{ color: 'var(--text-color)' }}>
                {hoverData.reason || '-'}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* พื้นหลังมืด (Backdrop) เฉพาะใน Mobile เมื่อเปิด Popup */}
      {hoverData && (
        <div
          className="md:hidden fixed inset-0 bg-black/60 backdrop-blur-sm z-[9998]"
          onClick={() => setHoverData(null)}
        />
      )}
    </div >
  );
};

export default RealtimePageV2;